

// Datei: src/slewo-beacon.js
/**
 * slewo-beacon.js v4.0 — Unkillable Signed Beacon + Heatmap
 *
 * Erfasst:
 * - Pageview (sofort, mit UTM/Click-IDs, Nav-Type, Page-Num)
 * - Scroll-Depth, Time-on-Page, Interaktion
 * - Mouse + Touch Kinematics (Bot-Detection)
 * - Rage Clicks, JS Errors (UX + Fraud)
 * - Tab-Visibility (echte sichtbare vs. versteckte Zeit)
 * - Web Vitals (LCP, CLS, INP, FCP, TTFB — einmal pro Seite)
 * - Browser-Fingerprint + Network-Info + UA Client Hints
 * - Add-to-Cart Klicks
 * - Click-Positionen + CSS-Selektoren (Heatmap)
 * - Scroll-Path-Verlauf (Scroll-Heatmap)
 * - AI-Referrer-Erkennung (ChatGPT, Perplexity, Claude, Gemini, Copilot)
 * - Dead-Click-Erkennung (Klick auf nicht-interaktives Element)
 * - Form-Abandon (visibilitychange + pagehide wenn Form aktiv)
 * - A/B-Variant-Tracking (window._sl_variant)
 *
 * v4.0 NEU (Heatmap-Erweiterungen):
 * - Click-Tracking: x%, y%, CSS-Selektor, Tag, Text-Hint, Rage-Flag pro Click
 * - Scroll-Path: Scroll-Position alle 2s als Array (statt nur Maximum)
 * - getSelector(): Minimaler CSS-Selektor (id > data-track > .class > tag:nth-child)
 * - Engagement-Heartbeat transportiert cl[] + sp[] (0 Bytes Overhead ohne Clicks)
 *
 * v3.2 Features (beibehalten):
 * - URL-Signatur: Signatur in /t/s?s=... statt im Body
 * - Server-Time Sync: window.__st kompensiert Client-Clock-Drift
 * - Offline-Trust: SW-retried Events bis 24h akzeptiert
 * - Self-Destruct Getter: window.__bk zerstoert sich beim ersten Lesen
 * - Rolling Pre-Signing: sendSync() ist IMMER signiert
 * - HMAC-SHA256 Signierung via Web Crypto API (Chain of Custody)
 * - 3-Tier Delivery: sendBeacon → fetch+keepalive → SW/IndexedDB
 * - Service Worker Beacon Relay (Background Sync)
 *
 * v4.3.1 Fix (14.02.2026):
 * - Soft-Nav Fallback: pushState/replaceState Monkey-Patch statt 1s-Polling
 *   Erkennt AJAX-Navigation sofort (0ms statt bis zu 1000ms Delay)
 *   Identisch mit GTM History Change Verhalten
 *
 * Heartbeat adaptiv (v4.4): 15s bei Aktivitaet, Backoff bis 60s bei Idle —
 * kein Datenverlust (kumulatives Payload + frisch-signierter Exit-Flush).
 * Hidden-Tab: Light-Heartbeat alle 60s (nur last_seen + time_hidden).
 * Kein Consent noetig (Art. 6(1)(f) DSGVO — Betrugsbekaempfung + berechtigtes Interesse).
 *
 * ~3.8 KB Brotli.
 */
(function() {
  'use strict';

  var ENDPOINT = '/t/c';
  var ENDPOINT_FALLBACK = '/t/s';
  var HEARTBEAT_INTERVAL = 15000;
  var HEARTBEAT_MAX_INTERVAL = 60000; // v4.4: Backoff-Kappe bei Inaktivitaet
  var HEARTBEAT_HIDDEN_INTERVAL = 60000;
  var PRESIGN_INTERVAL = 15000;       // v4.4: vorher 5000 — aligned mit Heartbeat (3x weniger Sign-/Build-Zyklen)
  var startTime = Date.now();
  var maxScroll = 0;
  var hasInteracted = false;
  var heartbeatTimer = null;
  var heartbeatCount = 0;
  var fpSent = false;
  var pvSent = false;
  var wvSent = false;
  var currentUrl = location.pathname + location.search;

  // Rage clicks
  var rageClicks = 0;
  var lastClickTime = 0;
  var rapidClickCount = 0;

  // JS errors
  var jsErrorCount = 0;

  // ERR-Signal-Kontrakt: effektiver Error-Count = max(eigener Zaehler,
  // window.__slErr.count). error.js (frueh geladen) fuellt window.__slErr auch
  // VOR dem Beacon-Load und bei CSP-Violations. Eigener Listener bleibt Fallback,
  // KEIN Doppelzaehlen (Maximum, nicht Summe). Tolerant falls __slErr fehlt.
  function _effErrCount() {
    var ext = (window.__slErr && typeof window.__slErr.count === 'number') ? window.__slErr.count : 0;
    return jsErrorCount > ext ? jsErrorCount : ext;
  }

  // Trigger der Sitzungsaufzeichnung ("js_error"): nur Ausnahmen aus eigenem Code (error.js zählt fp) und
  // eigene Skripte/Stylesheets, die nicht geladen wurden (rfp). Der Gesamtzähler (_effErrCount, ec) enthält
  // auch Tracking-Pixel, die absichtlich leer antworten (/detect.ajax, Bing-Pixel): Er stand in fast jeder
  // Sitzung auf 1–2 und startete die Aufzeichnung bei etwa jedem zweiten Besucher statt bei 2–5 %.
  // Fehlen die Klassenzähler (ältere error.js im Cache), bleibt es bei 0 — lieber keine Aufzeichnung.
  function _ownErrCount() {
    var s = window.__slErr;
    return s && typeof s === 'object' ? (s.fp | 0) + (s.rfp | 0) : 0;
  }

	  // Click-Tracking (Heatmap v4.0)
	  var clicks = [];
	  var MAX_CLICKS_PER_HB = 50;
	  var firstInputs = [];
	  var firstInputSent = false;
	  var mouseSeenBeforeInput = false;
	  var MAX_FIRST_INPUTS = 5;

  // Scroll-Path-Tracking (Heatmap v4.0)
  var scrollPath = [];
  var lastScrollSample = 0;
  var MAX_SCROLL_PER_HB = 30;

  // Cursor-Movement-Sampling (Heatmap v4.0 — 10% der Sessions)
  var cursorSample = false;
  var cursorBuf = [];
  var MAX_CURSOR_PER_HB = 300;
  try {
    var _csKey = '_cs';
    var stored = sessionStorage.getItem(_csKey);
    if (stored !== null) {
      cursorSample = stored === '1';
    } else {
      cursorSample = Math.random() < 0.10;
      sessionStorage.setItem(_csKey, cursorSample ? '1' : '0');
    }
  } catch(_) {}

  // Visibility tracking
  var timeVisible = 0;
  var timeHidden = 0;
  var tabSwitches = 0;
  var visStart = Date.now();

  // Web Vitals
  var _lcp = null;
  var _lcpEl = null;
  var _cls = 0;
  var _fcp = null;
  var _ttfb = null;
  var _inp = null;
  var _inpEl = null;
  var _lcpLd = null;
  var _tbt = null;
  // Erstes Verbergen der Seite in ms seit Navigationsbeginn (0 = im Hintergrund-Tab geladen). LCP/FCP einer
  // Seite, die erst nach Minuten sichtbar wurde, messen die Wartezeit und nicht die Seite; mit fh lässt sich
  // das herausfiltern (gültig: fh fehlt oder fh > lcp). Mobile Startseite: 12 % der Sitzungen zeigten LCP > 60 s.
  var _firstHidden = null;
  var _clsSrc = [];
  // INP-Phasen (A3): inputDelay / processingTime / presentationDelay des schlimmsten INP-Events
  var _inpId = null;
  var _inpPr = null;
  var _inpPd = null;
  // LoAF-Aggregat (A4): long-animation-frame Observer
  var _loafN = 0;
  var _loafBt = 0;
  var _loafMax = 0;
  var _loafSrc = '';
  var _loafSrcBt = 0;

  // Rolling Pre-Signed Payload (fuer sendSync — max 15s alt, aber SIGNIERT)
  // v3.2: Speichert {body, sig} statt eines einzelnen Strings
  var _preSigned = null;

  // ===================================================================
  // Key Protection: Self-Destruct Getter (v3.2)
  // window.__bk zerstoert sich beim ersten Lesen.
  // Wer zuerst liest (dieses Script) bekommt den Key.
  // Wer spaeter kommt (Extensions, Malware) bekommt undefined.
  // ===================================================================

  var _bk = window.__bk;
  try { delete window.__bk; } catch(_) { window.__bk = undefined; }

  // ===================================================================
  // Server-Time Sync (v3.2)
  // Kompensiert Client-Clock-Drift via PHP-injiziertem window.__st.
  // Alle _ts verwenden korrigierte Server-Zeit.
  // ===================================================================

  var _st = window.__st;
  try { delete window.__st; } catch(_) { window.__st = undefined; }
  var _timeOffset = _st ? (Math.floor(Date.now() / 1000) - _st) : 0;

  // Session-ID fuer Qualified Visit (orderId fuer spaetere Server-Adjustments)
  var _sid = window.__sid || '';
  try { delete window.__sid; } catch(_) { window.__sid = undefined; }

  function _serverTime() {
    return Math.floor(Date.now() / 1000) - _timeOffset;
  }

  // ===================================================================
  // HMAC-SHA256 Signing (Web Crypto API)
  // ===================================================================

  var _sigKey = null;

  function getSigningKey() {
    if (_sigKey) return Promise.resolve(_sigKey);
    if (!_bk || !crypto.subtle) return Promise.resolve(null);
    return crypto.subtle.importKey(
      'raw', new TextEncoder().encode(_bk),
      { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
    ).then(function(key) {
      _sigKey = key;
      return key;
    }).catch(function() { return null; });
  }

  function signPayload(data) {
    return getSigningKey().then(function(key) {
      if (!key) return null;
      return crypto.subtle.sign('HMAC', key, new TextEncoder().encode(data));
    }).then(function(sig) {
      if (!sig) return null;
      var arr = new Uint8Array(sig);
      var hex = '';
      for (var i = 0; i < arr.length; i++) {
        hex += (arr[i] < 16 ? '0' : '') + arr[i].toString(16);
      }
      return hex;
    }).catch(function() { return null; });
  }

  // ADR-0215 durable transport. The old worker never owns this queue.
  var _v2Enabled = window.__slDurableV2 === true;
  var _v2DBName = 'slewo-beacon-v2';
  var _v2Flushing = false;
  var _v2ExitRecord = null;
  var _v2ExitLegacy = false;
  var _v2ExitGeneration = 0;
  var _v2LastFailure = '';
  var _v2Confirmed = {};
  var _v2ConfirmedOrder = [];
  window.slewoTrackDelivery = function() {
    return { version: 2, last_failure: _v2LastFailure, queued_is_not_receipt: true };
  };

  function _v2Bytes(value) { return new TextEncoder().encode(value).length; }

  function _v2Eligible(payload) {
    return _v2Enabled && _sid && _bk && typeof fetch === 'function'
      && typeof indexedDB !== 'undefined' && typeof TextEncoder !== 'undefined'
      && typeof AbortController !== 'undefined' && window.crypto && crypto.subtle && crypto.getRandomValues
      && payload.type !== 'js' && payload.type !== 'error' && payload.type !== 'consent';
  }

  function _v2UUID() {
    var bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    var hex = Array.from(bytes, function(b) { return ('0' + b.toString(16)).slice(-2); }).join('');
    return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
  }

  function _v2Build(payload) {
    var item = Object.assign({}, payload);
    item.schema_version = 2;
    item.event_id = _v2UUID();
    item.sid = _sid;
    item.occurred_at_ms = Date.now() - _timeOffset * 1000;
    item._ts = _serverTime();
    item.url = typeof item.url === 'string' ? item.url : location.pathname + location.search;
    if (_v2Bytes(JSON.stringify(item.data === undefined ? null : item.data)) > 4096) return Promise.resolve(null);
    var body = JSON.stringify(item);
    if (_v2Bytes(body) > 16384) return Promise.resolve(null);
    return signPayload(body).then(function(sig) {
      if (!sig) return null;
      return {
        event_id: item.event_id, sid: item.sid, body: body, sig: sig,
        occurred_at_ms: item.occurred_at_ms, attempts: 0, next_ms: 0,
        lease_ms: 0, lease_token: '', state: 'pending', last_error: ''
      };
    });
  }

  function _v2Store(mode, operation) {
    return new Promise(function(resolve, reject) {
      var inactive = false;
      var open = indexedDB.open(_v2DBName, 1);
      open.onupgradeneeded = function() { open.result.createObjectStore('pending', { keyPath: 'event_id' }); };
      open.onerror = open.onblocked = function() { inactive = true; reject(new Error('storage_unavailable')); };
      open.onsuccess = function() {
        var db = open.result;
        if (inactive) { db.close(); return; }
        var tx = db.transaction('pending', mode);
        var result;
        var reason = 'storage_unavailable';
        tx.oncomplete = function() { db.close(); resolve(result); };
        tx.onabort = function() { db.close(); reject(new Error(reason)); };
        tx.onerror = function() { try { tx.abort(); } catch (_) {} };
        try {
          operation(tx.objectStore('pending'), function(value) { result = value; }, function(code) {
            reason = code;
            tx.abort();
          });
        } catch (_) { tx.abort(); }
      };
    });
  }

  function _v2ValidItem(item) {
    if (!item || typeof item.body !== 'string' || _v2Bytes(item.body) > 16384
        || typeof item.sig !== 'string' || !/^[a-f0-9]{64}$/.test(item.sig)
        || !Number.isInteger(item.occurred_at_ms) || !Number.isInteger(item.attempts)
        || item.attempts < 0 || item.attempts > 5) return false;
    try {
      var facts = JSON.parse(item.body);
      return facts.schema_version === 2 && facts.event_id === item.event_id && facts.sid === item.sid
        && facts.occurred_at_ms === item.occurred_at_ms;
    } catch (_) { return false; }
  }

  function _v2Admit(record) {
    return _v2Store('readwrite', function(store, done, fail) {
      if (!_v2ValidItem(record)) { fail('invalid_queue_record'); return; }
      var request = store.getAll(null, 51);
      request.onsuccess = function() {
        var items = request.result || [];
        var bytes = _v2Bytes(JSON.stringify(record));
        var count = 0;
        for (var i = 0; i < items.length; i++) {
          var item = items[i];
          if (!_v2ValidItem(item)) {
            if (item && item.event_id) {
              try { store.delete(item.event_id); } catch (_) {}
            }
            _v2LastFailure = 'invalid_dropped';
            continue;
          }
          if (_serverTime() * 1000 - item.occurred_at_ms > 86400000) {
            store.delete(item.event_id);
            _v2LastFailure = 'expired_unconfirmed';
            continue;
          }
          if (item.event_id === record.event_id) {
            if (item.body !== record.body || item.sig !== record.sig) fail('queue_identity_conflict');
            else done(item);
            return;
          }
          count++;
          bytes += _v2Bytes(JSON.stringify(item));
        }
        if (count >= 50 || bytes > 1048576) { fail('queue_capacity'); return; }
        store.add(record);
        done(record);
      };
    });
  }

  function _v2ReceiptMatches(receipt, record) {
    return receipt && receipt.schema_version === 2 && receipt.namespace === 'web-v2'
      && receipt.status === 'committed' && receipt.durable === true
      && Array.isArray(receipt.event_ids) && receipt.event_ids.length === 1
      && receipt.event_ids[0] === record.event_id;
  }

  function _v2Claim(id) {
    return _v2Store('readwrite', function(store, done, fail) {
      var request = store.get(id);
      request.onsuccess = function() {
        var item = request.result;
        if (!item) return;
        if (!_v2ValidItem(item)) { fail('invalid_queue_record'); return; }
        var now = _serverTime() * 1000;
        if (now - item.occurred_at_ms > 86400000) {
          _v2LastFailure = 'expired_unconfirmed';
          store.delete(id);
          return;
        }
        if (item.state !== 'pending' || item.attempts >= 5 || item.lease_ms > now || item.next_ms > now) return;
        item.attempts++;
        item.lease_ms = now + 10000;
        item.lease_token = _v2UUID();
        store.put(item);
        done(item);
      };
    });
  }

  function _v2Confirm(record) {
    return _v2Store('readwrite', function(store) {
      var request = store.get(record.event_id);
      request.onsuccess = function() {
        var current = request.result;
        if (current && current.body === record.body && current.sig === record.sig) {
          store.delete(record.event_id);
          _v2Confirmed[record.event_id] = true;
          _v2ConfirmedOrder.push(record.event_id);
          if (_v2ConfirmedOrder.length > 50) delete _v2Confirmed[_v2ConfirmedOrder.shift()];
        }
      };
    });
  }

  function _v2Failed(record, reason, terminal) {
    _v2LastFailure = reason;
    return _v2Store('readwrite', function(store) {
      var request = store.get(record.event_id);
      request.onsuccess = function() {
        var current = request.result;
        if (!current || current.body !== record.body || current.lease_token !== record.lease_token) return;
        current.last_error = reason;
        current.lease_ms = 0;
        current.next_ms = _serverTime() * 1000 + Math.min(60000, 1000 * Math.pow(2, current.attempts));
        if (terminal) current.state = 'terminal';
        else if (current.attempts >= 5) current.state = 'exhausted';
        store.put(current);
      };
    });
  }

  function _v2Dispatch(record) {
    var controller = new AbortController();
    var timeout = setTimeout(function() { controller.abort(); }, 4000);
    return Promise.resolve().then(function() { return fetch('/t/c?v=2&s=' + record.sig, {
      method: 'POST', body: record.body, headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin', mode: 'same-origin', redirect: 'error', cache: 'no-store',
      keepalive: true, signal: controller.signal
    }); }).then(function(response) {
      if (!response.ok) {
        return _v2Failed(record, 'http_' + response.status, response.status >= 400 && response.status < 500 && response.status !== 429);
      }
      return response.text().then(function(text) {
        if (text.length > 4096) return _v2Failed(record, 'unconfirmed_response', false);
        var receipt;
        try { receipt = JSON.parse(text); } catch (_) {}
        return _v2ReceiptMatches(receipt, record)
          ? _v2Confirm(record) : _v2Failed(record, 'unconfirmed_response', false);
      });
    }).catch(function() {
      return _v2Failed(record, 'network_unconfirmed', false);
    }).then(function() { clearTimeout(timeout); }, function() { clearTimeout(timeout); });
  }

  function _v2Flush() {
    if (!_v2Enabled || _v2Flushing) return Promise.resolve();
    _v2Flushing = true;
    return _v2Store('readonly', function(store, done) {
      var request = store.getAll(null, 51);
      request.onsuccess = function() { done(request.result || []); };
    }).then(function(items) {
      if (items.length > 50) throw new Error('invalid_queue_record');
      var chain = Promise.resolve();
      var dispatched = 0;
      items.sort(function(a, b) { return a.occurred_at_ms - b.occurred_at_ms; });
      items.forEach(function(item) {
        chain = chain.then(function() {
          if (dispatched >= 5) return null;
          return _v2Claim(item.event_id).then(function(record) {
            if (!record) return null;
            dispatched++;
            return _v2Dispatch(record);
          });
        });
      });
      return chain;
    }).catch(function() { _v2LastFailure = 'storage_unavailable'; }).then(function() { _v2Flushing = false; });
  }

  function _v2Queue(record) {
    if (_v2Confirmed[record.event_id]) return Promise.resolve(true);
    return _v2Admit(record).then(function() {
      _v2Flush();
      return true;
    });
  }

  function _v2Send(payload) {
    _v2Flush();
    return _v2Build(payload).then(function(record) {
      if (!record) return false;
      return _v2Queue(record);
    }).catch(function(error) {
      _v2LastFailure = error && error.message === 'queue_capacity'
        ? 'queue_capacity_before_send' : 'storage_unavailable_before_send';
      return false;
    });
  }

  if (_v2Enabled) {
    window.addEventListener('online', _v2Flush);
    _v2Flush();
  }
  // End ADR-0215 durable transport.

  // ===================================================================
  // 3-Tier Delivery: sendBeacon → fetch+keepalive → SW/IndexedDB
  // v3.2: Signatur geht in URL (/t/s?s=...) statt in den Body
  // ===================================================================

  function _url(sig, fallback) {
    var ep = fallback ? ENDPOINT_FALLBACK : ENDPOINT;
    return sig ? ep + '?s=' + sig : ep;
  }

  function _dispatch(data, sig) {
    var url = _url(sig);

    // Tier 1: sendBeacon — synchron, ueberlebt Navigation
    if (navigator.sendBeacon) {
      try { if (navigator.sendBeacon(url, data)) return; }
      catch(_) {}
    }

    // Tier 2: fetch+keepalive — async, SW intercepted bei Fehler
    if (typeof fetch === 'function') {
      try {
        fetch(url, {
          method: 'POST', body: data,
          headers: { 'Content-Type': 'application/json' },
          keepalive: true
        }).catch(function() {
          // Fallback-Endpoint versuchen bevor IndexedDB
          var fbUrl = _url(sig, true);
          fetch(fbUrl, {
            method: 'POST', body: data,
            headers: { 'Content-Type': 'application/json' },
            keepalive: true
          }).catch(function() { queueRetry(data, sig); });
        });
        return;
      } catch(_) {}
    }

    // Tier 3: IndexedDB — SW retries via Background Sync
    queueRetry(data, sig);
  }

  // Exit-Dispatch: sendBeacon → fetchLater → fetch (fuer pagehide/visibilitychange)
  function _dispatchExit(data, sig) {
    var url = _url(sig);

    // Prefer sendBeacon (immediate, survives navigation)
    if (navigator.sendBeacon) {
      try { if (navigator.sendBeacon(url, data)) return; }
      catch(_) {}
    }

    // fetchLater as backup (Chromium 121+, guaranteed on page destroy)
    if (typeof fetchLater === 'function') {
      try { fetchLater(url, { method: 'POST', body: data }); return; }
      catch(_) {}
    }

    // Last resort: fetch (might not complete on page close)
    if (typeof fetch === 'function') {
      try { fetch(url, { method: 'POST', body: data, keepalive: true }).catch(function() {}); }
      catch(_) {}
    }
  }

  // v3.2: send() — Signatur geht NIE in den Body, sondern in die URL
  // Body = exakt der signierte String (kein doppeltes Serialisieren)
  function send(payload) {
    payload._ts = _serverTime();
    if (_v2Eligible(payload)) {
      _v2Send(payload).then(function(handled) {
        // Before the first v2 attempt only, preserve legacy best effort.
        if (!handled) {
          var legacy = JSON.stringify(payload);
          signPayload(legacy).then(function(sig) { _dispatch(legacy, sig); });
        }
      }).catch(function() {
        var legacy = JSON.stringify(payload);
        signPayload(legacy).then(function(sig) { _dispatch(legacy, sig); });
      });
      return;
    }
    var data = JSON.stringify(payload);

    signPayload(data).then(function(sig) {
      _dispatch(data, sig);
    });
  }

  // Exit-Send: Verwendet pre-signiertes Payload (Rolling Pre-Signing)
  // Keine async-Crypto noetig — Signatur wurde im Hintergrund vorbereitet
  function sendSync() {
    if (_v2Eligible({ type: 'engagement' }) && _v2ExitLegacy) return;
    if (_v2ExitRecord && _v2Enabled) {
      _v2Flush();
      return;
    }
    if (_preSigned && _preSigned.v2) {
      _v2ExitRecord = _preSigned.record;
      _preSigned = null;
      _v2Queue(_v2ExitRecord).catch(function() { _v2LastFailure = 'exit_storage_unconfirmed'; });
      _flushExitWv();
      return;
    }
    if (_v2Eligible({ type: 'engagement' })) _v2ExitLegacy = true;
    if (_preSigned) {
      _dispatchExit(_preSigned.body, _preSigned.sig);
      _preSigned = null;
      _flushExitWv();
      return;
    }
    // Fallback: unsigned (nur in den ersten ~15s nach Seitenaufruf)
    var payload = buildEngagementPayload();
    payload._ts = _serverTime();
    _dispatchExit(JSON.stringify(payload), null);
    _flushExitWv();
  }

  // A1: Web-Vitals-Exit-Flush — schliesst den Bounce-Blindspot.
  // Sendet beim Exit (visibilitychange=hidden / pagehide) EINMAL pro Seite einen
  // finalen Web-Vitals-Snapshot in einem separaten Feld (d.wv mit wvp=1), auch
  // wenn heartbeatCount==0 — also auch fuer Bouncer, die nie einen Heartbeat-wv
  // senden. wvSent wird hier konsumiert, damit kein Doppel-Snapshot entsteht.
  // Unsigned via Exit-Dispatch (Server persistiert generisch); das separate Feld
  // triggert den Heartbeat-wv-Pfad nicht.
  // Geraetetyp fuer beide Sendepfade. Bewusst eine gemeinsame Funktion:
  // buildEngagementPayload() und _flushExitWv() bauen ihr Payload getrennt,
  // und genau dadurch fehlte dt im Exit-Pfad monatelang.
  function _detectDeviceType() {
    if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') {
      return navigator.userAgentData.mobile ? 'mobile' : 'desktop';
    }
    return _isMobile ? 'mobile' : 'desktop';
  }

  var _exitWvSent = false;
  function _flushExitWv() {
    if (_exitWvSent || wvSent) return;
    _exitWvSent = true;
    wvSent = true;
    try {
      var d = {
        top: Math.round((Date.now() - startTime) / 1000),
        hb: heartbeatCount,
        dt: _detectDeviceType(),
        wv: _buildWv({ isExit: true })
      };
      var payload = { type: 'engagement', data: d, url: location.pathname };
      payload._ts = _serverTime();
      _dispatchExit(JSON.stringify(payload), null);
    } catch(_) {}
  }

  // ===================================================================
  // Rolling Pre-Signing: Alle 5s Engagement-Payload signieren
  // v3.2: Speichert {body, sig} — Signatur und Body getrennt
  // ===================================================================

  function _updatePreSigned() {
    var payload = buildEngagementPayload();
    if (_v2Eligible(payload)) {
      var generation = _v2ExitGeneration;
      return _v2Build(payload).then(function(record) {
        if (record && generation === _v2ExitGeneration && !_v2ExitRecord && !_v2ExitLegacy) {
          _preSigned = { v2: true, record: record };
        }
      });
    }
    payload._ts = _serverTime();
    var data = JSON.stringify(payload);
    // v4.4: Promise zurueckgeben — visibilitychange(hidden) kann auf die
    // frische Signatur awaiten statt ein ≤15s altes Presign zu senden.
    return signPayload(data).then(function(sig) {
      if (sig) {
        _preSigned = { body: data, sig: sig };
      }
    });
  }

  // ===================================================================
  // Retry Queue: IndexedDB (SW-accessible)
  // v3.2: Speichert sig separat (fuer URL-basierte Signatur beim Retry)
  // ===================================================================

  var IDB_NAME = 'slewo-beacon';
  var IDB_STORE = 'retry';

  function _openIDB() {
    return new Promise(function(resolve, reject) {
      var req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = function(e) {
        var db = e.target.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) {
          db.createObjectStore(IDB_STORE, { keyPath: 'id', autoIncrement: true });
        }
      };
      req.onsuccess = function(e) { resolve(e.target.result); };
      req.onerror = function() { reject(req.error); };
    });
  }

  function queueRetry(data, sig) {
    try {
      _openIDB().then(function(db) {
        var tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE).add({ body: data, sig: sig || '', ts: Date.now(), retries: 0 });
        // Trigger Background Sync if SW is active
        if (navigator.serviceWorker && navigator.serviceWorker.ready) {
          navigator.serviceWorker.ready.then(function(reg) {
            if (reg.sync) reg.sync.register('beacon-retry').catch(function() {});
          });
        }
      }).catch(function() {});
    } catch(_) {}
  }

  // ===================================================================
  // Hilfsfunktionen
  // ===================================================================

  function getPageNum() {
    try {
      var n = parseInt(sessionStorage.getItem('_pn') || '0', 10) + 1;
      sessionStorage.setItem('_pn', String(n));
      return n;
    } catch(_) { return 1; }
  }

  function getUTM() {
    try {
      var p = new URLSearchParams(location.search);
      var keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
      var utm = {};
      var hasNew = false;
      for (var i = 0; i < keys.length; i++) {
        var v = p.get(keys[i]);
        if (v) { utm[keys[i].replace('utm_', '')] = v; hasNew = true; }
      }
      // Pinterest-Erkennung: Feed-Klicks kommen mit utm_source=base, aber Pinterest-Referer
      if (utm.source === 'base' && /pinterest\.|pin\.it/i.test(document.referrer)) {
        utm.source = 'pinterest';
      }
      var stored = JSON.parse(sessionStorage.getItem('_utm') || '{}');
      var merged = {};
      var k;
      for (k in stored) merged[k] = stored[k];
      for (k in utm) merged[k] = utm[k];
      if (hasNew) sessionStorage.setItem('_utm', JSON.stringify(merged));
      return Object.keys(merged).length ? merged : null;
    } catch(_) { return null; }
  }

  function getClickIds() {
    try {
      var p = new URLSearchParams(location.search);
      var ids = ['gclid', 'fbclid', 'msclkid', 'ttclid', 'wbraid', 'gbraid', 'epik'];
      var result = null;
      for (var i = 0; i < ids.length; i++) {
        var v = p.get(ids[i]);
        if (v) {
          if (!result) result = {};
          result[ids[i]] = v;
          sessionStorage.setItem('_' + ids[i], v);
        }
      }
      if (!result) {
        for (var j = 0; j < ids.length; j++) {
          var sv = sessionStorage.getItem('_' + ids[j]);
          if (sv) {
            if (!result) result = {};
            result[ids[j]] = sv;
          }
        }
      }
      return result;
    } catch(_) { return null; }
  }

  // AI-Referrer-Erkennung (Traffic VON ChatGPT, Perplexity, Claude etc.)
  var AI_REFERRERS = [
    { p: /chatgpt\.com|openai\.com/, n: 'ChatGPT' },
    { p: /perplexity\.ai/, n: 'Perplexity' },
    { p: /gemini\.google\.com/, n: 'Gemini' },
    { p: /claude\.ai/, n: 'Claude' },
    { p: /copilot\.microsoft\.com|edgeservices\.bing/, n: 'Copilot' },
    { p: /you\.com/, n: 'You.com' },
    { p: /phind\.com/, n: 'Phind' }
  ];

  function getAiSource() {
    try {
      // Einmal pro Session speichern
      var stored = sessionStorage.getItem('_ais');
      if (stored) return stored;
      var ref = document.referrer;
      if (!ref) return null;
      for (var i = 0; i < AI_REFERRERS.length; i++) {
        if (AI_REFERRERS[i].p.test(ref)) {
          sessionStorage.setItem('_ais', AI_REFERRERS[i].n);
          return AI_REFERRERS[i].n;
        }
      }
      return null;
    } catch(_) { return null; }
  }

  // A/B-Variant auslesen (Cookie _sl_ab oder window._sl_variant)
  function getVariant() {
    try {
      if (window._sl_variant) return window._sl_variant;
      var sv = sessionStorage.getItem('_slv');
      if (sv) return sv;
      // Cookie _sl_ab lesen (PHP-gesetzt, 50/50 Split)
      var m = document.cookie.match(/(?:^|;\s*)_sl_ab=([^;]+)/);
      if (m) return m[1];
      return null;
    } catch(_) { return null; }
  }

  // Dead-Click: Pruefen ob Element interaktiv ist
  function _isInteractive(el) {
    if (!el || !el.tagName) return true;
    var tag = el.tagName;
    if (tag === 'A' || tag === 'BUTTON' || tag === 'INPUT' ||
        tag === 'SELECT' || tag === 'TEXTAREA' || tag === 'LABEL') return true;
    if (el.getAttribute('role') === 'button' || el.getAttribute('role') === 'link') return true;
    if (el.getAttribute('onclick') || el.getAttribute('tabindex')) return true;
    if (el.closest('a, button, label, [role="button"]')) return true;
    try { if (getComputedStyle(el).cursor === 'pointer') return true; } catch(_) {}
    return false;
  }

  function getNavType() {
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      return nav ? nav.type : null;
    } catch(_) { return null; }
  }

  // ===================================================================
  // Heatmap: Minimaler CSS-Selektor fuer Click-Targets
  // Prioritaet: id > data-track > .class-Kombination > tag:nth-child
  // Max 200 Zeichen, max 4 Ebenen, keine dynamischen Klassen
  // ===================================================================

  var _dynClassRe = /^(js-|no-|is-|has-|active|hover|focus|open|show|hide|visible|hidden|collapsed|expanded|selected|disabled|loading|animat)/;

	  function _getSelector(el) {
	    if (!el || el === document.body || el === document.documentElement) return 'body';
    var parts = [];
    var cur = el;
    for (var depth = 0; depth < 4 && cur && cur !== document.body; depth++) {
      // ID — eindeutig, sofort fertig
      if (cur.id && /^[a-zA-Z][\w-]*$/.test(cur.id)) {
        parts.unshift('#' + cur.id);
        break;
      }
      // data-track Attribut
      var dt = cur.getAttribute('data-track');
      if (dt) {
        parts.unshift('[data-track="' + dt.substring(0, 30) + '"]');
        break;
      }
      // Klassen (ohne dynamische)
      var tag = cur.tagName.toLowerCase();
      var classes = [];
      if (cur.classList) {
        for (var i = 0; i < cur.classList.length && classes.length < 3; i++) {
          var c = cur.classList[i];
          if (c.length > 1 && c.length <= 30 && !_dynClassRe.test(c)) {
            classes.push('.' + c);
          }
        }
      }
      if (classes.length) {
        parts.unshift(tag + classes.join(''));
      } else {
        // tag:nth-child als Fallback
        var idx = 1;
        var sib = cur.previousElementSibling;
        while (sib) { idx++; sib = sib.previousElementSibling; }
        parts.unshift(tag + ':nth-child(' + idx + ')');
      }
      cur = cur.parentElement;
    }
	    var sel = parts.join(' > ');
	    return sel.length > 200 ? sel.substring(0, 200) : sel;
	  }

	  function _keyClass(e) {
	    var k = e && e.key ? String(e.key) : '';
	    if (!k) return '';
	    if (k.length === 1) return 'char';
	    if (/^(Enter|Space|Tab|Escape)$/.test(k)) return k.toLowerCase();
	    if (/^Arrow/.test(k)) return 'nav';
	    if (/^(Backspace|Delete)$/.test(k)) return 'edit';
	    return 'other';
	  }

	  function _recordFirstInput(e, type) {
	    if (firstInputSent || firstInputs.length >= MAX_FIRST_INPUTS) return;
	    e = e || {};
	    var el = e.target || null;
	    var item = {
	      ty: type,
	      t: Math.max(0, Math.round(Date.now() - startTime)),
	      tr: e.isTrusted === false ? 0 : 1,
	      tg: el && el.tagName ? el.tagName.toLowerCase() : '',
	      dd: _isInteractive(el) ? 0 : 1
	    };
	    if (firstInputs.length === 0) {
	      item.vs = document.visibilityState || '';
	      item.pm = mouseSeenBeforeInput ? 1 : 0;
	    }
	    if (type === 'click' || type === 'pointer' || type === 'touch') {
	      item.pt = e.pointerType || (type === 'touch' ? 'touch' : 'mouse');
	      if (typeof e.button === 'number') item.bt = e.button;
	      if (typeof e.detail === 'number') item.dt = e.detail;
	      // B6: Pointer-Physik NUR bei echtem Touch-Input werten — Maus liefert
	      // pressure=0.5/0 legitim. Nur bei isTrusted, pointerType='touch' und
	      // tatsaechlich vorhandener Property (synthetische Events haben pressure=0.5
	      // konstant, width=1). tr/pt/bt/dt bleiben unveraendert.
	      if (e.isTrusted === true && item.pt === 'touch') {
	        if (typeof e.pressure === 'number') item.pr = Math.round(e.pressure * 1000) / 1000;
	        if (typeof e.width === 'number' && typeof e.height === 'number') {
	          item.tw = Math.round(Math.max(e.width, e.height) * 100) / 100;
	        }
	        if (typeof e.tiltX === 'number') item.tlt = e.tiltX;
	      }
	    }
	    if (type === 'key') item.kc = _keyClass(e);
	    firstInputs.push(item);
	    if (firstInputs.length === 1) {
	      try { _updatePreSigned(); } catch(_) {}
	    }
	  }

  // ===================================================================
  // 1. Pageview (sofort — Server weiss welche Seite + Attribution)
  // ===================================================================

  function sendPageview() {
    if (pvSent) return;
    pvSent = true;
    var d = {
      r: document.referrer ? document.referrer.replace(/https?:\/\/[^\/]+/, '').split('?')[0] : '',
      t: document.title.substring(0, 80),
      nt: getNavType(),
      pn: getPageNum(),
      hl: history.length
    };
    var utm = getUTM();
    if (utm) d.utm = utm;
    var cid = getClickIds();
    if (cid) d.cid = cid;
    var ais = getAiSource();
    if (ais) d.ais = ais;
    var abv = getVariant();
    if (abv) {
      d.abv = abv;
      try { sessionStorage.setItem('_slv', abv); } catch(_) {}
    }
    send({ type: 'pv', data: d, url: location.pathname + location.search });
  }

  // Prerender-Aware Boot:
  // Chrome (und andere) laden Anzeigen-Landingpages spekulativ vor.
  // Waehrend des Prerenderings ist `document.prerendering === true` und
  // alle Engagement-Messungen (time_visible, scroll, kinematics) waeren
  // verfaelscht — das ist kein echter User-Besuch.
  //
  // Loesung: Pageview-Versand, startTime und visStart erst bei der
  // tatsaechlichen Aktivierung der Seite setzen. Wenn der User die
  // prerenderte Seite nie aktiviert (haeufiger Fall), wird auch nie
  // ein Pageview an den Server geschickt — kein Geister-Tracking.
  if (document.prerendering) {
    document.addEventListener('prerenderingchange', function onActivate() {
      document.removeEventListener('prerenderingchange', onActivate);
      // Zeitbasis auf Aktivierungszeit zuruecksetzen, damit
      // time_visible/time_on_page erst ab JETZT zaehlen.
      startTime = Date.now();
      visStart  = Date.now();
      sendPageview();
    }, { once: true });
  } else {
    sendPageview();
  }

  // ===================================================================
  // 2. Scroll-Depth (passiv, kein Performance-Impact)
  // ===================================================================

  var scrollRaf = false;
  window.addEventListener('scroll', function() {
    if (scrollRaf) return;
    scrollRaf = true;
    requestAnimationFrame(function() {
      scrollRaf = false;
      var docH = document.documentElement.scrollHeight;
      if (docH <= 0) return;
      var pct = Math.round((window.scrollY + window.innerHeight) / docH * 100);
      if (pct > maxScroll) maxScroll = Math.min(pct, 100);

      // Heatmap v4.0: Scroll-Path (alle 2s samplen)
      var now = Date.now();
      if (now - lastScrollSample >= 2000 && scrollPath.length < MAX_SCROLL_PER_HB) {
        lastScrollSample = now;
        scrollPath.push({
          p: Math.min(pct, 100),
          t: Math.round((now - startTime) / 1000)
        });
      }
    });
  }, { passive: true });

  // ===================================================================
  // 3. Interaktion + Rage-Click-Detection
  // ===================================================================

	  document.addEventListener('click', function(e) {
	    hasInteracted = true;
	    _recordFirstInput(e, 'click');
	    var now = Date.now();
    var isRage = false;
    if (now - lastClickTime < 800) {
      rapidClickCount++;
      if (rapidClickCount >= 3) {
        rageClicks++;
        rapidClickCount = 0;
        isRage = true;
      }
    } else {
      rapidClickCount = 0;
    }
    lastClickTime = now;

    // Heatmap v4.0: Click-Position + Selektor erfassen
    if (clicks.length < MAX_CLICKS_PER_HB) {
      var docH = document.documentElement.scrollHeight || 1;
      var vw = window.innerWidth || 1;
      var vh = window.innerHeight || 1;
      var el = e.target;
      var txt = '';
      try { txt = (el.textContent || '').trim().substring(0, 50); } catch(_) {}
      clicks.push({
        x: Math.round(e.clientX / vw * 10000) / 10000,
        y: Math.round((e.clientY + window.scrollY) / docH * 10000) / 10000,
        vw: vw,
        vh: vh,
        ph: docH,
        sl: _getSelector(el),
        tg: el.tagName ? el.tagName.toLowerCase() : '',
        tx: txt,
        rg: isRage ? 1 : 0,
        dd: _isInteractive(el) ? 0 : 1,
        t: Math.round((now - startTime) / 1000)
      });
    }
  }, { passive: true });

	  document.addEventListener('pointerdown', function(e) {
	    hasInteracted = true;
	    _recordFirstInput(e, 'pointer');
	  }, { passive: true });

	  document.addEventListener('keydown', function(e) {
	    hasInteracted = true;
	    _recordFirstInput(e, 'key');
	  }, { passive: true });

  // ===================================================================
  // 4. JS Errors zaehlen + Details erfassen (UX + Fraud-Signal)
  // ===================================================================

  var jsErrors = [];
  var JS_ERROR_MAX = 5;

  function isNonActionableJsError(data) {
    var msg = (data.msg || '').trim();
    var src = (data.src || '').trim();
    var stack = (data.stack || '').trim();
    var line = data.line || 0;
    var col = data.col || 0;
    if (/^(chrome|moz|safari)-extension:|^webkit-masked-url:/i.test(src)) return true;
    return /^Script error\.?$/i.test(msg) && !src && !line && !col && !stack;
  }

  window.addEventListener('error', function(e) {
    var errorData = {
      msg: (e.message || '').substring(0, 200),
      src: (e.filename || '').substring(0, 100),
      line: e.lineno || 0,
      col: e.colno || 0,
      stack: (e.error && e.error.stack) ? e.error.stack.substring(0, 500) : ''
    };
    if (isNonActionableJsError(errorData)) return;
    jsErrorCount++;
    if (jsErrors.length < JS_ERROR_MAX) {
      jsErrors.push(errorData);
      send({ type: 'error', data: jsErrors[jsErrors.length - 1] });
    }
  });

  window.addEventListener('unhandledrejection', function(e) {
    var reason = e.reason || {};
    var errorData = {
      msg: (typeof reason === 'string' ? reason : (reason.message || 'Unhandled Promise')).substring(0, 200),
      src: '',
      line: 0,
      col: 0,
      stack: (reason.stack || '').substring(0, 500)
    };
    if (isNonActionableJsError(errorData)) return;
    jsErrorCount++;
    if (jsErrors.length < JS_ERROR_MAX) {
      jsErrors.push(errorData);
      send({ type: 'error', data: jsErrors[jsErrors.length - 1] });
    }
  });

  // ===================================================================
  // 5. Mouse Kinematics (10Hz, 50-point Buffer)
  // ===================================================================

	  var mousePos = [];
	  var lastMouseT = 0;
	  document.addEventListener('mousemove', function(e) {
	    var now = Date.now();
	    if (now - lastMouseT < 100) return;
	    lastMouseT = now;
	    _markActivity(); // v4.4: Maus-Aktivitaet resettet Heartbeat-Backoff
	    mouseSeenBeforeInput = true;
	    if (mousePos.length >= 50) mousePos.shift();
    mousePos.push({ x: e.clientX, y: e.clientY, t: now });

    // Heatmap v4.0: Cursor-Positionen fuer Move-Heatmap (10% Sampling)
    if (cursorSample && cursorBuf.length < MAX_CURSOR_PER_HB) {
      var docH = document.documentElement.scrollHeight || 1;
      var vw = window.innerWidth || 1;
      cursorBuf.push({
        x: Math.round(e.clientX / vw * 10000) / 10000,
        y: Math.round((e.clientY + window.scrollY) / docH * 10000) / 10000,
        t: Math.round((now - startTime))
      });
    }
  }, { passive: true });

  // ===================================================================
  // 6. Touch Kinematics (10Hz, 50-point Buffer)
  // ===================================================================

	  var touchPos = [];
	  var lastTouchT = 0;
	  var touchCount = 0;
	  document.addEventListener('touchstart', function(e) {
	    touchCount++;
	    hasInteracted = true;
	    _recordFirstInput(e, 'touch');
	  }, { passive: true });
  document.addEventListener('touchmove', function(e) {
    var now = Date.now();
    if (now - lastTouchT < 100) return;
    lastTouchT = now;
    var t = e.touches[0];
    if (!t) return;
    if (touchPos.length >= 50) touchPos.shift();
    touchPos.push({ x: t.clientX, y: t.clientY, t: now });
  }, { passive: true });

  function computeKinematics(pts) {
    var n = pts.length;
    if (n < 3) return null;
    var velocities = [];
    var totalDist = 0;
    var dirChanges = 0;
    var prevAngle = null;
    for (var i = 1; i < n; i++) {
      var dx = pts[i].x - pts[i-1].x;
      var dy = pts[i].y - pts[i-1].y;
      var dt = pts[i].t - pts[i-1].t;
      var dist = Math.sqrt(dx*dx + dy*dy);
      totalDist += dist;
      if (dt > 0) velocities.push(dist / dt);
      var angle = Math.atan2(dy, dx);
      if (prevAngle !== null) {
        var diff = Math.abs(angle - prevAngle);
        if (diff > Math.PI) diff = 2 * Math.PI - diff;
        if (diff > 0.5) dirChanges++;
      }
      prevAngle = angle;
    }
    var sum = 0;
    for (var j = 0; j < velocities.length; j++) sum += velocities[j];
    var mean = velocities.length ? sum / velocities.length : 0;
    var sqDiff = 0;
    for (var k = 0; k < velocities.length; k++) sqDiff += (velocities[k] - mean) * (velocities[k] - mean);
    var std = velocities.length ? Math.sqrt(sqDiff / velocities.length) : 0;
    var straight = Math.sqrt(Math.pow(pts[n-1].x - pts[0].x, 2) + Math.pow(pts[n-1].y - pts[0].y, 2));
    var eff = totalDist > 0 ? straight / totalDist : 0;
    return {
      v: Math.round(mean * 1000) / 1000,
      s: Math.round(std * 1000) / 1000,
      d: dirChanges,
      e: Math.round(eff * 100) / 100
    };
  }

  // ===================================================================
  // 7. Web Vitals (LCP, CLS, INP, FCP, TTFB)
  // ===================================================================

  try {
    if (typeof PerformanceObserver !== 'undefined') {
      new PerformanceObserver(function(list) {
        var entries = list.getEntries();
        if (entries.length) {
          var _le = entries[entries.length - 1];
          _lcp = Math.round(_le.startTime);
          if (_le.loadTime) _lcpLd = Math.round(_le.loadTime);
          var _el = _le.element;
          if (_el && _el.tagName) {
            var _s = _el.tagName;
            if (_el.id) _s += '#' + _el.id;
            else if (_el.className && _el.className.split) _s += '.' + _el.className.split(' ')[0];
            var _src = _el.currentSrc || _el.src;
            if (_src) _s += '@' + _src.split('/').pop().split('?')[0];
            _lcpEl = _s.slice(0, 120);
          }
        }
      }).observe({ type: 'largest-contentful-paint', buffered: true });

      // FCP laufend beobachten: Der einmalige Lesezugriff weiter unten verfehlt jede Seite, die beim
      // Start des Beacons noch nicht gezeichnet hatte (Hintergrund-Tab, langsames Gerät) — dort fehlte
      // der FCP bei vorhandenem LCP. Der Lesezugriff bleibt als Rückfall für Browser ohne paint-Observer.
      try {
        new PerformanceObserver(function(list) {
          var entries = list.getEntries();
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].name === 'first-contentful-paint') _fcp = Math.round(entries[i].startTime);
          }
        }).observe({ type: 'paint', buffered: true });
      } catch(_) {}

      new PerformanceObserver(function(list) {
        var entries = list.getEntries();
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].hadRecentInput) continue;
          _cls += entries[i].value;
          if (entries[i].value > 0.05 && entries[i].sources) {
            for (var _j = 0; _j < entries[i].sources.length && _clsSrc.length < 5; _j++) {
              var _sn = entries[i].sources[_j].node;
              if (_sn && _sn.tagName) _clsSrc.push((_sn.tagName + (_sn.id ? '#' + _sn.id : '')).slice(0, 30));
            }
          }
        }
      }).observe({ type: 'layout-shift', buffered: true });

      new PerformanceObserver(function(list) {
        var entries = list.getEntries();
        for (var i = 0; i < entries.length; i++) {
          var d = entries[i].processingEnd - entries[i].startTime;
          if (_inp === null || d > _inp) {
            _inp = Math.round(d);
            var _it = entries[i].target;
            if (_it && _it.tagName) _inpEl = (_it.tagName + (_it.id ? '#' + _it.id : (_it.className && _it.className.split ? '.' + _it.className.split(' ')[0] : ''))).slice(0, 60);
            // A3: INP-Phasen aus dem schlimmsten Event ableiten.
            // inputDelay = processingStart - startTime, processingTime = processingEnd - processingStart,
            // presentationDelay = duration - (processingEnd - startTime). presentationDelay >=0 clampen.
            var _ev = entries[i];
            var _ps = _ev.processingStart;
            var _pe = _ev.processingEnd;
            var _stt = _ev.startTime;
            if (typeof _ps === 'number' && typeof _stt === 'number') {
              _inpId = Math.max(0, Math.round(_ps - _stt));
            }
            if (typeof _ps === 'number' && typeof _pe === 'number') {
              _inpPr = Math.max(0, Math.round(_pe - _ps));
            }
            if (typeof _ev.duration === 'number' && typeof _pe === 'number' && typeof _stt === 'number') {
              _inpPd = Math.max(0, Math.round(_ev.duration - (_pe - _stt)));
            }
          }
        }
      }).observe({ type: 'event', buffered: true, durationThreshold: 16 });

      try {
        new PerformanceObserver(function(list) {
          var entries = list.getEntries();
          for (var i = 0; i < entries.length; i++) {
            if (_tbt === null) _tbt = 0;
            _tbt += Math.max(0, Math.round(entries[i].duration) - 50);
          }
        }).observe({ type: 'longtask', buffered: true });
      } catch(_) {}

      // A4: LoAF (long-animation-frame, Chrome 123+) — zweiter passiver Observer.
      // Aggregat ueber alle Frames: Anzahl, Summe blockingDuration, groesster Frame,
      // Top-Script-Quelle (nur Dateiname). Per-Frame-Listen werden NICHT gesammelt.
      try {
        new PerformanceObserver(function(list) {
          var entries = list.getEntries();
          for (var i = 0; i < entries.length; i++) {
            var f = entries[i];
            var bd = Math.max(0, Math.round(f.blockingDuration || 0));
            _loafN++;
            _loafBt += bd;
            if (bd > _loafMax) _loafMax = bd;
            // Script-Quelle des teuersten Scripts dieses Frames bestimmen.
            if (f.scripts && f.scripts.length) {
              for (var sj = 0; sj < f.scripts.length; sj++) {
                var sc = f.scripts[sj];
                var scbt = Math.max(0, Math.round((sc.duration || 0)));
                if (scbt > _loafSrcBt) {
                  _loafSrcBt = scbt;
                  var srcUrl = sc.sourceURL || sc.invoker || '';
                  if (srcUrl) {
                    _loafSrc = String(srcUrl).split('/').pop().split('?')[0].slice(0, 60);
                  }
                }
              }
            }
          }
        }).observe({ type: 'long-animation-frame', buffered: true });
      } catch(_) {}
    }
  } catch(_) {}

	  try {
	    var paintEntries = performance.getEntriesByName('first-contentful-paint');
	    if (paintEntries.length) _fcp = Math.round(paintEntries[0].startTime);
	    var navEntries = performance.getEntriesByType('navigation');
	    if (navEntries.length) _ttfb = Math.round(navEntries[0].responseStart - navEntries[0].requestStart);
	  } catch(_) {}

	  // Erstes Verbergen (siehe _firstHidden): Chromium führt die Übergänge seit Version 115 als
	  // "visibility-state"-Einträge, auch rückwirkend. Sonst gilt, was der Beacon selbst sieht.
	  try {
	    var vsEntries = performance.getEntriesByType('visibility-state');
	    for (var vi = 0; vi < vsEntries.length; vi++) {
	      if (vsEntries[vi].name === 'hidden') { _firstHidden = Math.round(vsEntries[vi].startTime); break; }
	    }
	  } catch(_) {}
	  if (_firstHidden === null && document.visibilityState === 'hidden') _firstHidden = 0;
	  if (_firstHidden === null) {
	    document.addEventListener('visibilitychange', function _onFirstHide() {
	      if (document.visibilityState !== 'hidden') return;
	      _firstHidden = Math.round(performance.now());
	      document.removeEventListener('visibilitychange', _onFirstHide);
	    });
	  }

	  function getResourceSummary() {
	    try {
	      var entries = performance.getEntriesByType('resource');
	      if (!entries || !entries.length) return null;
	      var out = { rc: 0, tr: 0, enc: 0, dec: 0, zc: 0, js: 0, css: 0, img: 0, font: 0 };
	      for (var i = 0; i < entries.length && i < 300; i++) {
	        var e = entries[i];
	        out.rc++;
	        out.tr += Math.max(0, e.transferSize || 0);
	        out.enc += Math.max(0, e.encodedBodySize || 0);
	        out.dec += Math.max(0, e.decodedBodySize || 0);
	        if ((e.transferSize || 0) === 0) out.zc++;
	        if (e.initiatorType === 'script') out.js++;
	        else if (e.initiatorType === 'css' || e.initiatorType === 'link') out.css++;
	        else if (e.initiatorType === 'img') out.img++;
	        else if (e.initiatorType === 'font') out.font++;
	      }
	      out.tr = Math.round(out.tr / 1024);
	      out.enc = Math.round(out.enc / 1024);
	      out.dec = Math.round(out.dec / 1024);
	      return out;
	    } catch(_) { return null; }
	  }

  // ===================================================================
  // 8. Engagement-Daten sammeln + senden
  // ===================================================================

  // Soft-Nav-/BFCache-Marker fuer das wv-JSON (A6/A7) — rein lokal, additiv.
  var _wvSoftNav = false;   // true ab erstem onUrlChange (Soft-Nav-Fenster)
  var _wvNav = null;        // z.B. 'bfcache' bei BFCache-Restore (A7)
  var _wvBfRt = null;       // BFCache-Restore-Latenz in ms (A7)

  // A2: Navigation-Timing-Breakdown aus dem navigation-Entry ableiten.
  // Cross-Origin-Redirect-Werte sind oft 0 (Opaqueness) — 0 != fehlend.
  // nextHopProtocol wird bewusst weggelassen.
  function _navTimingBreakdown() {
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      if (!nav) return null;
      var nt = {};
      function _put(key, val) {
        if (typeof val === 'number' && isFinite(val) && val >= 0) nt[key] = Math.round(val);
      }
      _put('dns', nav.domainLookupEnd - nav.domainLookupStart);
      _put('tcp', nav.connectEnd - nav.connectStart);
      // TLS nur wenn secureConnectionStart gesetzt (>0)
      if (nav.secureConnectionStart && nav.secureConnectionStart > 0) {
        _put('tls', nav.connectEnd - nav.secureConnectionStart);
      }
      _put('redirect', nav.redirectEnd - nav.redirectStart);
      _put('req', nav.responseStart - nav.requestStart);
      _put('res', nav.responseEnd - nav.responseStart);
      _put('dcl', nav.domContentLoadedEventEnd - nav.startTime);
      _put('load', nav.loadEventEnd - nav.startTime);
      _put('dom', nav.domComplete - nav.responseEnd);
      // Kompressionsrate (Dokument selbst)
      if (typeof nav.encodedBodySize === 'number') nt.doc_enc = Math.round(nav.encodedBodySize);
      if (typeof nav.decodedBodySize === 'number') nt.doc_dec = Math.round(nav.decodedBodySize);
      return Object.keys(nt).length ? nt : null;
    } catch(_) { return null; }
  }

  // Baut das wv-JSON inkl. der additiven Breakdowns (A2-A7).
  // opts.isExit=true → Exit-Flush-Variante (A1): setzt wvp=1.
  function _buildWv(opts) {
    var isExit = opts && opts.isExit;
    var wv = { cls: Math.round(_cls * 1000) / 1000 };
    // LCP nur fuer Hard-Loads (nicht fuer Soft-Navs — A6).
    if (!_wvSoftNav && _lcp !== null) wv.lcp = _lcp;
    if (!_wvSoftNav && _lcpEl) wv.lcp_el = _lcpEl;
    if (_fcp !== null) wv.fcp = _fcp;
    if (_ttfb !== null) wv.ttfb = _ttfb;
    if (_inp !== null) wv.inp = _inp;
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      if (nav && nav.activationStart) wv.as = Math.round(nav.activationStart);
    } catch(_) {}
    var rs = getResourceSummary();
    if (rs) wv.rs = rs;
    if (_lcpLd !== null) wv.lcp_ld = _lcpLd;
    if (!_wvSoftNav && _firstHidden !== null) wv.fh = _firstHidden;
    if (_inpEl) wv.inp_el = _inpEl;
    if (_tbt !== null) wv.tbt = _tbt;

    // Fehlerklassen aus error.js (nur Werte > 0): fp eigener Code, tp Fremdskripte, ext Erweiterung/In-App,
    // unk nicht zuordenbar, res Ladefehler gesamt, rfp/rimg eigene Skripte+Stylesheets / Bilder, csp Richtlinie.
    // ec bleibt davon unberührt (Gesamtzähler, speist Betrugserkennung und Auswertungen).
    var _es = window.__slErr;
    if (!_wvSoftNav && _es && typeof _es === 'object') {
      var _eo = null;
      var _ek = ['fp', 'tp', 'ext', 'unk', 'res', 'rfp', 'rimg', 'csp'];
      for (var _ei = 0; _ei < _ek.length; _ei++) {
        if (_es[_ek[_ei]] > 0) {
          if (!_eo) _eo = {};
          _eo[_ek[_ei]] = _es[_ek[_ei]] | 0;
        }
      }
      if (_eo) wv.e = _eo;
    }
    if (_clsSrc.length) wv.cls_src = _clsSrc.slice(0, 5);

    // A2: Navigation-Timing-Breakdown (nur Hard-Load, Soft-Navs haben keinen neuen nav-Entry).
    if (!_wvSoftNav) {
      var nt = _navTimingBreakdown();
      if (nt) wv.nt = nt;
    }

    // A3: INP-Phasen-Breakdown.
    if (_inpId !== null || _inpPr !== null || _inpPd !== null) {
      var inpP = {};
      if (_inpId !== null) inpP.id = _inpId;
      if (_inpPr !== null) inpP.pr = _inpPr;
      if (_inpPd !== null) inpP.pd = _inpPd;
      wv.inp_p = inpP;
    }

    // A4: LoAF-Attribution (nur falls Frames beobachtet).
    if (_loafN > 0) {
      var loaf = { n: _loafN, bt: _loafBt, max: _loafMax };
      if (_loafSrc) loaf.src = _loafSrc;
      wv.loaf = loaf;
    }

    // A5: LCP-Phasen NUR sicher ableitbar (Hard-Load).
    // lcp_rd = renderDelay = lcp - lcp_ld (nur wenn lcp_ld > 0), lcp_ttfb = ttfb.
    if (!_wvSoftNav) {
      if (_lcp !== null && _lcpLd !== null && _lcpLd > 0) {
        wv.lcp_rd = Math.max(0, _lcp - _lcpLd);
      }
      if (_ttfb !== null) wv.lcp_ttfb = _ttfb;
    }

    // A6: Soft-Nav-Marker.
    if (_wvSoftNav) wv.sn = 1;

    // A7: BFCache-Restore-Marker + Restore-Latenz (lokal im wv-JSON, NICHT nav_type umdeuten).
    if (_wvNav) wv.nav = _wvNav;
    if (_wvBfRt !== null) wv.bf = { rt: _wvBfRt };

    // A1: Exit-Flush markieren.
    if (isExit) wv.wvp = 1;

    return wv;
  }

  function getVisibleTime() {
    var elapsed = Date.now() - visStart;
    return document.visibilityState !== 'hidden'
      ? { vis: timeVisible + elapsed, hid: timeHidden }
      : { vis: timeVisible, hid: timeHidden + elapsed };
  }

  // forSend=true → echter Heartbeat-/Soft-Nav-Send, der wvSent konsumieren darf.
  // forSend=false (Default, z.B. Presign-Build alle 15s) → wvSent NICHT setzen,
  // sonst unterdrueckt der Presign-Build den echten Heartbeat-Send (A1).
  function buildEngagementPayload(forSend) {
    var timeOnPage = Math.round((Date.now() - startTime) / 1000);
    var mk = computeKinematics(mousePos);
    var tk = computeKinematics(touchPos);
    var vt = getVisibleTime();

    var d = {
      top: timeOnPage,
      sd: maxScroll,
      ia: hasInteracted ? 1 : 0,
      hb: heartbeatCount,
      // Mouse kinematics
      mm: mousePos.length,
      mv: mk ? mk.v : 0,
      ms: mk ? mk.s : 0,
      md: mk ? mk.d : 0,
      me: mk ? mk.e : 0,
      // Touch kinematics
      tc: touchCount,
      tv: tk ? tk.v : 0,
      ts: tk ? tk.s : 0,
      // Visibility + Attention
      tiv: Math.round(vt.vis / 1000),
      tih: Math.round(vt.hid / 1000),
      tw: tabSwitches,
      // UX + Fraud signals
      rc: rageClicks,
      ec: _effErrCount()
    };

    // Web Vitals — einmal pro Seite (beim ersten Heartbeat, dann stabil)
	    if (!wvSent && heartbeatCount >= 1) {
	      // wvSent NUR bei echtem Send konsumieren (A1) — Presign-Build (forSend=false)
	      // darf das Heartbeat-Gate nicht verbrauchen.
	      if (forSend) wvSent = true;
	      d.dt = _detectDeviceType();
	      d.wv = _buildWv({ isExit: false });
	    }

	    if (!firstInputSent && firstInputs.length) {
	      d.fi = firstInputs.slice();
	      if (forSend) {
	        firstInputs = [];
	        firstInputSent = true;
	      }
	    }

    // Heatmap v4.0: Click-Koordinaten + Scroll-Path + Cursor-Movement
    if (clicks.length) {
      d.cl = clicks.slice();
      if (forSend) clicks = [];
    }
    if (scrollPath.length) {
      d.sp = scrollPath.slice();
      if (forSend) scrollPath = [];
    }
    if (cursorBuf.length) {
      d.cur = {
        pos: cursorBuf.slice(),
        vw: window.innerWidth || 0,
        vh: window.innerHeight || 0,
        ph: document.documentElement.scrollHeight || 0
      };
      if (forSend) cursorBuf = [];
    }

    return { type: 'engagement', data: d, url: location.pathname };
  }

  // ===================================================================
  // 9. Heartbeat — Engagement senden (Crash-Schutz)
  //    v4.4: Adaptiver Takt — 15s bei Aktivitaet, Backoff 15→30→60s wenn
  //    seit dem letzten Beat keine Aktivitaet (Scroll/Klick/Tastatur/Touch).
  //    Payload ist kumulativ + Exit-Flush (hidden/pagehide) sendet immer den
  //    finalen Stand — keine Daten gehen durch den Backoff verloren.
  //    Hidden-Tab: Light-Heartbeat alle 60s (nur last_seen + Zeiten)
  // ===================================================================

  var _hiddenTimer = null;
  var _hbInterval = HEARTBEAT_INTERVAL;
  var _lastActivity = Date.now();

  // Aktivitaets-Marker: setzt den Takt bei neuer Aktivitaet sofort auf 15s
  // zurueck (inkl. Re-Scheduling, damit aktive User keinen 60s-Beat warten).
  function _markActivity() {
    _lastActivity = Date.now();
    if (_hbInterval !== HEARTBEAT_INTERVAL) {
      _hbInterval = HEARTBEAT_INTERVAL;
      if (heartbeatTimer && document.visibilityState !== 'hidden') {
        clearTimeout(heartbeatTimer);
        heartbeatTimer = setTimeout(_heartbeatTick, HEARTBEAT_INTERVAL);
      }
    }
  }

  function _heartbeatTick() {
    heartbeatTimer = null;
    var top = Math.round((Date.now() - startTime) / 1000);
    if (top >= 5) {
      heartbeatCount++;
      send(buildEngagementPayload(true));
      // v4.4: Presign aligned zum Beat frisch halten (Exit-Payload ≤ Beat-Alter)
      _updatePreSigned();
      // rrweb Trigger-Check bei jedem Heartbeat (Rage-Clicks, JS-Errors)
      var recTrigger = _shouldRecord();
      if (recTrigger) _startRecording(recTrigger);
      // Backoff nur bei Inaktivitaet seit dem letzten Beat
      if (Date.now() - _lastActivity >= _hbInterval) {
        _hbInterval = Math.min(_hbInterval * 2, HEARTBEAT_MAX_INTERVAL);
      } else {
        _hbInterval = HEARTBEAT_INTERVAL;
      }
    }
    if (document.visibilityState !== 'hidden') {
      heartbeatTimer = setTimeout(_heartbeatTick, _hbInterval);
    }
  }

  function startHeartbeat() {
    if (heartbeatTimer) return;
    heartbeatTimer = setTimeout(_heartbeatTick, _hbInterval);
  }

  // Aktivitaets-Listener fuer den Backoff (passiv, billig: 1 Vergleich im Hot-Path)
  window.addEventListener('scroll', _markActivity, { passive: true });
  document.addEventListener('pointerdown', _markActivity, { passive: true });
  document.addEventListener('keydown', _markActivity, { passive: true });
  document.addEventListener('touchstart', _markActivity, { passive: true });

  function stopHeartbeat() {
    clearTimeout(heartbeatTimer);
    heartbeatTimer = null;
  }

  // Light-Heartbeat: Nur Zeiten aktualisieren wenn Tab im Hintergrund
  function startHiddenHeartbeat() {
    if (_hiddenTimer) return;
    _hiddenTimer = setInterval(function() {
      var vt = getVisibleTime();
      send({
        type: 'engagement',
        data: {
          top: Math.round((Date.now() - startTime) / 1000),
          sd: maxScroll,
          ia: hasInteracted ? 1 : 0,
          hb: heartbeatCount,
          mm: 0, mv: 0, ms: 0, md: 0, me: 0,
          tc: 0, tv: 0, ts: 0,
          tiv: Math.round(vt.vis / 1000),
          tih: Math.round(vt.hid / 1000),
          tw: tabSwitches,
          rc: rageClicks,
          ec: _effErrCount()
        },
        url: location.pathname
      });
    }, HEARTBEAT_HIDDEN_INTERVAL);
  }

  function stopHiddenHeartbeat() {
    clearInterval(_hiddenTimer);
    _hiddenTimer = null;
  }

  document.addEventListener('visibilitychange', function() {
    var now = Date.now();
    if (document.visibilityState === 'visible') {
      _v2ExitGeneration++;
      _v2ExitRecord = null;
      _v2ExitLegacy = false;
      timeHidden += now - visStart;
      visStart = now;
      _hbInterval = HEARTBEAT_INTERVAL; // v4.4: Backoff bei Rueckkehr resetten
      stopHiddenHeartbeat();
      startHeartbeat();
      startPreSigning();
    } else {
      timeVisible += now - visStart;
      visStart = now;
      tabSwitches++;
      stopHeartbeat();
      // v4.4: Exit-Payload FRISCH signieren (0s statt ≤15s alt) und erst dann
      // senden — Promise loest in ~1ms, Seite lebt bei hidden noch voll.
      // Safety-Timeout 300ms: sendet notfalls mit letztem Presign/unsigned.
      var exitSent = false;
      // visibilityState-Guard: feuert der 300ms-Safety-Timer erst nach einem
      // bfcache-Restore (dann visible), wird kein Rausch-Event gesendet.
      var doSendSync = function() { if (!exitSent && document.visibilityState === 'hidden') { exitSent = true; sendSync(); } };
      try {
        var presignP = _updatePreSigned();
        if (presignP && typeof presignP.then === 'function') presignP.then(doSendSync, doSendSync);
      } catch(_) {}
      setTimeout(doSendSync, 300);
      startHiddenHeartbeat();
    }
  });

  window.addEventListener('pagehide', function() {
    stopHeartbeat();
    stopHiddenHeartbeat();
    sendSync();
  });

  if (document.visibilityState !== 'hidden') {
    startHeartbeat();
  } else {
    startHiddenHeartbeat();
  }

  // ===================================================================
  // 9b. Rolling Pre-Signing Timer (v4.4: alle 15s statt 5s — aligned mit
  //     Heartbeat; zusaetzlich frisch bei jedem Beat und bei hidden)
  // Damit sendSync() immer ein signiertes Payload hat
  // ===================================================================

  var _preSignTimer = null;

  function startPreSigning() {
    if (_preSignTimer) return;
    _updatePreSigned(); // Sofort erstes Pre-Signed erzeugen
    _preSignTimer = setInterval(function() {
      if (document.visibilityState === 'hidden') return;
      // v4.4: bei aktivem Heartbeat-Backoff (>15s) reicht der Post-Beat-Presign —
      // der Intervall-Presign wuerde sonst Heatmap-Puffer (cl/sp/cur) in Bodies
      // drainen, die vor dem naechsten Beat ueberschrieben werden.
      if (_hbInterval > PRESIGN_INTERVAL) return;
      _updatePreSigned();
    }, PRESIGN_INTERVAL);
  }

  function stopPreSigning() {
    clearInterval(_preSignTimer);
    _preSignTimer = null;
  }

  // Pre-Signing nach Pageview starten (sofort verfuegbar)
  startPreSigning();

  // ===================================================================
  // 9b. (entfernt 2026-04-13) — Qualified Visit Smart-Bidding Signal
  //     Frueher feuerte hier ein eigener "qualified_visit" Event mit
  //     value=0.25 an sGTM und an den DataLayer. Inzwischen laeuft die
  //     Mikro-Conversion-Pipeline ueber page_view_micro / add_to_cart_micro
  //     / begin_checkout_micro: der DataLayer-Interceptor in content_kopf.php
  //     ruft /t/audit auf, t_audit_handler.php schreibt den Initial-Send
  //     in shop_conversion_audit, und class.MicroConversion.php adjustiert
  //     den Value spaeter ueber die Google-Ads-API. Der separate Beacon-
  //     Event war redundant und ist daher entfernt.
  //
  //     Nicht verwechseln: shop_conversion_audit.event_type='qualified_visit'
  //     ist ein interner Marker-String fuer Initial-Sends, NICHT mit dem
  //     hier entfernten GA4-Event identisch. Siehe analytics.md
  //     (Engagement-Filter & Browser-Spekulation) und
  //     google-ads-micro-conversions.md.
  // ===================================================================

  // ===================================================================
  // 10. Soft-Navigation Detection (Navigation API + Fallback)
  //     Shop nutzt AJAX + history.pushState → kein Full-Page-Reload
  // ===================================================================

  function resetPageState() {
    startTime = Date.now();
    maxScroll = 0;
    hasInteracted = false;
    heartbeatCount = 0;
    _hbInterval = HEARTBEAT_INTERVAL; // v4.4: Backoff bei Soft-Nav/bfcache resetten
    _lastActivity = Date.now();
    rageClicks = 0;
    rapidClickCount = 0;
    jsErrorCount = 0;
    timeVisible = 0;
    timeHidden = 0;
    tabSwitches = 0;
    visStart = Date.now();
	    wvSent = false;
	    _exitWvSent = false;
	    // wv-Marker fuer den neuen Seitenzyklus neutralisieren (onUrlChange/pageshow
	    // setzen Soft-Nav- bzw. BFCache-Marker danach gezielt neu).
	    _wvSoftNav = false;
	    _wvNav = null;
	    _wvBfRt = null;
	    pvSent = false;
	    firstInputs = [];
	    firstInputSent = false;
	    mouseSeenBeforeInput = false;
	    mousePos = [];
    touchPos = [];
    touchCount = 0;
    clicks = [];
    scrollPath = [];
    lastScrollSample = 0;
    cursorBuf = [];
    _preSigned = null;
    _v2ExitRecord = null;
    _v2ExitLegacy = false;
    _v2ExitGeneration++;
    _formStarted = {};
    _formSubmitted = {};
    _fieldTimers = {};
    currentUrl = location.pathname + location.search;
  }

  function onUrlChange() {
    var newUrl = location.pathname + location.search;
    if (newUrl === currentUrl) return;
    // Finales Engagement fuer die ALTE Seite senden (mit alter URL)
    var payload = buildEngagementPayload(true);
    payload.url = currentUrl;
    send(payload);
    // Alles zuruecksetzen fuer neue Seite
    resetPageState();
    // A6: Soft-Nav-Web-Vitals — NUR die kumulativen Vitals-Akkumulatoren neu fenstern.
    // Observer-Init und der erste Hard-Load-Send bleiben unangetastet; LCP wird
    // fuer Soft-Navs weggelassen (Marker wv.sn=1 in _buildWv).
    _wvSoftNav = true;
    _wvNav = null;
    _wvBfRt = null;
    _cls = 0;
    _clsSrc = [];
    _inp = null;
    _inpEl = null;
    _tbt = null;
    _inpId = null;
    _inpPr = null;
    _inpPd = null;
    _exitWvSent = false;
    // Neuen Pageview senden
    sendPageview();
    // Suche per getlink (Suchfeld) oder weitergeleitete Suche — Abschnitt 14
    if (typeof trackSearch === 'function') { try { trackSearch(true); } catch(_) {} }
    // page_view an GTM/GA4 senden (Soft-Navigation)
    // Noetig weil getlink() nur __slBeaconPV() aufruft, nicht tagmanagerPushEvent
    // pvKey()-Dedup in content_kopf.php verhindert Double-Counting (Key = URL)
    if (typeof window.tagmanagerPushEvent === 'function') {
      window.tagmanagerPushEvent({ event: 'page_view' });
    }
    // Sofort Pre-Signed fuer neue Seite vorbereiten
    _updatePreSigned();
  }

  // Navigation API (Chrome 102+, Edge 102+) — erkennt pushState, replaceState, popstate
  if (window.navigation && typeof navigation.addEventListener === 'function') {
    navigation.addEventListener('currententrychange', onUrlChange);
  } else {
    // Fallback: Monkey-Patch pushState/replaceState (wie GTM intern)
    // Feuert onUrlChange sofort bei URL-Aenderung statt 1s-Polling
    var origPush = history.pushState;
    var origReplace = history.replaceState;
    history.pushState = function() {
      origPush.apply(this, arguments);
      onUrlChange();
    };
    history.replaceState = function() {
      origReplace.apply(this, arguments);
      onUrlChange();
    };
    window.addEventListener('popstate', onUrlChange);
  }

  // Globale API fuer AJAX-Navigation (getlink)
  // Wird von getlink() nach pushState aufgerufen — kein Polling/Detection noetig
  window.__slBeaconPV = function() {
    onUrlChange();
  };

  // bfcache: Seite aus Browser-Cache wiederhergestellt
  window.addEventListener('pageshow', function(e) {
    if (e.persisted) {
      send(buildEngagementPayload(true));
      resetPageState();
      sendPageview();
      // A7: BFCache-Restore-Vitals — Restore-Latenz via doppeltem rAF messen,
      // Marker wv.nav='bfcache' lokal im wv-JSON. resetPageState() hat _wvNav/_wvBfRt
      // bereits genullt; hier neu fuer den Restore-Zyklus setzen.
      _wvNav = 'bfcache';
      var _bfStart = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      try {
        requestAnimationFrame(function() {
          requestAnimationFrame(function() {
            var _now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
            _wvBfRt = Math.max(0, Math.round(_now - _bfStart));
          });
        });
      } catch(_) {}
      startHeartbeat();
      startPreSigning();
    }
  });

  // ===================================================================
  // 11. Add-to-Cart Klick
  // ===================================================================

  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.btn-warenkorb, [data-action="add-to-cart"], .add_to_cart');
    if (!btn) return;
    send({
      type: 'atc',
      data: {
        anr: btn.dataset.anr || (btn.closest('[data-anr]') || {}).dataset && btn.closest('[data-anr]').dataset.anr || '',
        atrnr: btn.dataset.atrnr || (btn.closest('[data-atrnr]') || {}).dataset && btn.closest('[data-atrnr]').dataset.atrnr || ''
      },
      url: location.pathname
    });
  }, { passive: true });

  // ===================================================================
  // 12. Form Analytics (Checkout-Formulare — DSGVO: keine Feldwerte)
  // ===================================================================

  var _formStarted = {};  // form_id → true (einmal pro Formular)
  var _fieldTimers = {};  // field_name → focus-Timestamp

  // Nur Checkout-Formulare tracken
  function _isCheckoutForm(form) {
    if (!form || !form.tagName || form.tagName !== 'FORM') return false;
    // getAttribute statt .action — verhindert DOM-Clobbering wenn ein Input name="action" hat
    var a = (form.getAttribute('action') || '').toLowerCase();
    var id = (form.id || '').toLowerCase();
    var cls = (typeof form.className === 'string' ? form.className : '').toLowerCase();
    return a.indexOf('kasse') >= 0 || a.indexOf('checkout') >= 0 ||
           a.indexOf('basket') >= 0 || id.indexOf('checkout') >= 0 ||
           cls.indexOf('checkout') >= 0 || cls.indexOf('kasse') >= 0;
  }

  function _getFormId(form) {
    var act = form.getAttribute('action') || '';
    return form.id || form.name || act.split('?')[0].split('/').pop() || 'unknown';
  }

  // form_start: Erster Focus auf ein Formularfeld
  document.addEventListener('focusin', function(e) {
    var el = e.target;
    if (!el || !el.form || !_isCheckoutForm(el.form)) return;
    var formId = _getFormId(el.form);
    var fieldName = el.name || el.id || el.type || 'unknown';

    // Feld-Timer starten
    _fieldTimers[fieldName] = Date.now();

    // form_start nur einmal pro Formular
    if (!_formStarted[formId]) {
      _formStarted[formId] = true;
      send({
        type: 'form_start',
        data: { fid: formId },
        url: location.pathname
      });
    }
  }, { passive: true });

  // form_field: Blur auf Feld — Dauer + Korrektur
  document.addEventListener('focusout', function(e) {
    var el = e.target;
    if (!el || !el.form || !_isCheckoutForm(el.form)) return;
    var fieldName = el.name || el.id || el.type || 'unknown';
    var startT = _fieldTimers[fieldName];
    if (!startT) return;

    var timeSpent = Date.now() - startT;
    delete _fieldTimers[fieldName];

    // Nur sinnvolle Felder tracken (keine hidden, submit, button)
    var t = (el.type || '').toLowerCase();
    if (t === 'hidden' || t === 'submit' || t === 'button') return;

    send({
      type: 'form_field',
      data: {
        fid: _getFormId(el.form),
        fn: fieldName.substring(0, 50),
        ft: t.substring(0, 20),
        ms: Math.min(timeSpent, 300000)
      },
      url: location.pathname
    });
  }, { passive: true });

  // form_submit — merken dass Formular erfolgreich abgeschickt wurde
  var _formSubmitted = {};
  document.addEventListener('submit', function(e) {
    var form = e.target;
    if (!form || !_isCheckoutForm(form)) return;
    var fid = _getFormId(form);

    // Clientseitig blockierte Submits (z.B. fehlende AGB/Payment-Auswahl)
    // duerfen nicht als echte Checkout-Submits in den Funnel laufen.
    if (e.defaultPrevented) {
      if (form.classList && form.classList.contains('kasse_checkout')) {
        _formSubmitted[fid] = true;
        send({
          type: 'form_submit',
          data: { fid: fid, mode: 'ajax' },
          url: location.pathname
        });
        return;
      }
      send({
        type: 'form_submit_blocked',
        data: { fid: fid, reason: 'client_validation' },
        url: location.pathname
      });
      return;
    }

    _formSubmitted[fid] = true;
    send({
      type: 'form_submit',
      data: { fid: fid },
      url: location.pathname
    });
  }, { passive: true });

  // form_abandon — visibilitychange wenn Form gestartet aber nicht submitted
  function _checkFormAbandon() {
    for (var fid in _formStarted) {
      if (_formStarted[fid] && !_formSubmitted[fid]) {
        send({
          type: 'form_abandon',
          data: { fid: fid, src: 'client' },
          url: location.pathname
        });
      }
    }
  }

  document.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'hidden') _checkFormAbandon();
  });
  window.addEventListener('pagehide', _checkFormAbandon);

  // ===================================================================
  // 12b. Session Recording (rrweb — Lazy-Load + Trigger)
  // Nur 2-5% der Sessions: Checkout, Rage-Clicks, JS-Errors, Random 2%
  // DSGVO: Passwort/Email/Telefon maskiert, Zahlungsdaten geblockt
  // ===================================================================

  var _rrwebLoading = false;
  // Nach einem Ladefehler (Werbeblocker, Netz) nicht bei jedem Heartbeat erneut versuchen.
  var _rrwebFailed = false;
  var _rrwebActive = false;
  var _rrwebStop = null;
  var _rrwebEvents = [];
  var _rrwebTrigger = null;
  var _RRWEB_MAX_EVENTS = 1000;
  var _RRWEB_CHUNK_SIZE = 200;

  function _shouldRecord() {
    // Bereits aktiv, im Laden oder fehlgeschlagen?
    if (_rrwebActive || _rrwebLoading || _rrwebFailed) return null;
    // URL-Parameter: Admin-Modus
    try { if (new URLSearchParams(location.search).get('_record') === '1') return 'manual'; } catch(_) {}
    // Checkout-Seite
    var path = location.pathname.toLowerCase();
    if (path.indexOf('/warenkorb') >= 0 || path.indexOf('/kasse') >= 0 || path.indexOf('/checkout') >= 0) return 'checkout';
    // Rage-Clicks (>=2)
    if (rageClicks >= 2) return 'rage_click';
    // JS-Errors aus eigenem Code (nicht der Gesamtzähler ec — siehe _ownErrCount)
    if (_ownErrCount() > 0) return 'js_error';
    // Zufaellig 2%
    try {
      var _recKey = '_rec';
      var stored = sessionStorage.getItem(_recKey);
      if (stored === '1') return 'random';
      if (stored === '0') return null;
      var shouldRec = Math.random() < 0.02;
      sessionStorage.setItem(_recKey, shouldRec ? '1' : '0');
      if (shouldRec) return 'random';
    } catch(_) {}
    return null;
  }

  function _startRecording(trigger) {
    if (_rrwebActive || _rrwebLoading) return;
    _rrwebLoading = true;
    _rrwebTrigger = trigger;

    // Lazy-Load rrweb-record.min.js
    var script = document.createElement('script');
    script.src = '/static/js/vendor/rrweb-record.min.js';
    script.onload = function() {
      _rrwebLoading = false;
      if (typeof rrweb === 'undefined' || typeof rrweb.record !== 'function') return;
      _rrwebActive = true;

      _rrwebStop = rrweb.record({
        sampling: { mousemove: 50, scroll: 150, input: 'last' },
        blockSelector: '.sl-privacy, .payment-form, input[type=password], .cc-field',
        maskInputOptions: { password: true, email: true, tel: true },
        blockClass: 'no-record',
        emit: function(event) {
          if (!_rrwebActive) return;
          _rrwebEvents.push(event);

          // Chunk senden wenn Buffer voll
          if (_rrwebEvents.length >= _RRWEB_CHUNK_SIZE) {
            _sendRecordingChunk(false);
          }

          // Limit: Recording stoppen
          if (_rrwebEvents.length >= _RRWEB_MAX_EVENTS && _rrwebStop) {
            _sendRecordingChunk(true);
            _rrwebStop();
            _rrwebActive = false;
          }
        }
      });
    };
    script.onerror = function() { _rrwebLoading = false; _rrwebFailed = true; };
    document.head.appendChild(script);
  }

  function _sendRecordingChunk(isFinal) {
    if (!_rrwebEvents.length) return;
    var chunk = _rrwebEvents.splice(0, _RRWEB_CHUNK_SIZE);
    var payload = JSON.stringify({
      events: chunk,
      trigger: _rrwebTrigger,
      page_url: location.pathname,
      final: !!isFinal
    });

    // Recording-Chunks gehen direkt an /api/recording/chunk (nicht /t/s)
    if (navigator.sendBeacon) {
      try {
        navigator.sendBeacon('/api/recording/chunk', new Blob([payload], { type: 'application/json' }));
        return;
      } catch(_) {}
    }
    if (typeof fetch === 'function') {
      try {
        fetch('/api/recording/chunk', {
          method: 'POST', body: payload,
          headers: { 'Content-Type': 'application/json' },
          keepalive: true
        }).catch(function() {});
      } catch(_) {}
    }
  }

  // Trigger-Check: nach 5s und nach jedem Heartbeat
  setTimeout(function() {
    var trigger = _shouldRecord();
    if (trigger) _startRecording(trigger);
  }, 5000);

  // Finaler Flush bei Seitenende
  window.addEventListener('pagehide', function() {
    if (_rrwebActive && _rrwebEvents.length) {
      _sendRecordingChunk(true);
    }
  });

  // ===================================================================
  // 13. Browser-Fingerprint + Network + UA Client Hints
  // ===================================================================

  // Der Fingerprint lief als ein einziger Task (WebGL-Kontext, Canvas, Audio-Kontext) — im Feld der größte Frame
  // des Beacons (Ø 547 ms in 474 mobilen Sitzungen am 23./24.10.2026). Inhalt, Reihenfolge und Startzeitpunkt
  // bleiben (fp_hash darf sich nicht ändern); nur zwischen den schweren Teilen gibt die Funktion den
  // Hauptthread ab. scheduler.yield() setzt die Arbeit danach mit Vorrang fort, sonst ein neuer Task.
  function _fpYield(fn) {
    try {
      if (window.scheduler && typeof window.scheduler.yield === 'function') {
        window.scheduler.yield().then(fn, fn);
        return;
      }
    } catch(_) {}
    // Rückfall (Safari, Firefox, ältere Chromium): ein MessageChannel-Task statt setTimeout(0). Timer werden in
    // Hintergrund-Tabs auf mindestens 1 s gedrosselt, Nachrichten nicht — vier Stufen verzögern den Versand dort sonst um Sekunden.
    try {
      if (typeof MessageChannel === 'function') {
        var ch = new MessageChannel();
        ch.port1.onmessage = function() {
          ch.port1.onmessage = null;
          try { ch.port1.close(); } catch(_) {}
          fn();
        };
        ch.port2.postMessage(0);
        return;
      }
    } catch(_) {}
    setTimeout(fn, 0);
  }

  function sendFingerprint() {
    if (fpSent) return;
    fpSent = true;

    var fp = {
      // Bot-Detection
      wd: navigator.webdriver === true ? 1 : 0,
      // Hardware
      hc: navigator.hardwareConcurrency || 0,
      dm: navigator.deviceMemory || 0,
      cd: screen.colorDepth || 0,
      sr: screen.width + 'x' + screen.height,
      // Browser
      ll: navigator.languages ? navigator.languages.length : 0,
      tp: navigator.maxTouchPoints || 0,
      pl: navigator.plugins ? navigator.plugins.length : 0,
      pf: navigator.platform || '',
      tz: new Date().getTimezoneOffset(),
      lang: navigator.language || '',
      ol: navigator.onLine ? 1 : 0,
      // Device
      dpr: window.devicePixelRatio || 0,
      aw: screen.availWidth || 0,
      ah: screen.availHeight || 0,
      ow: window.outerWidth || 0,
      oh: window.outerHeight || 0,
      // Privacy/Capabilities
      dnt: navigator.doNotTrack || '0',
      ce: navigator.cookieEnabled ? 1 : 0,
      pv: typeof navigator.pdfViewerEnabled !== 'undefined' ? (navigator.pdfViewerEnabled ? 1 : 0) : -1
    };

    // Funktionsweite Handles der async-Proben: sendFp() (Audio-Teil) wartet auf sie.
    var highEntropyPromise = null;
    var permQueryPromise = null;

    // B1: IANA-Timezone (z.B. 'Europe/Berlin') — verraet die Host-Region auch
    // wenn ein Proxy-Bot den numerischen Offset (tz) faked. Reines data-Feld,
    // vom fp_hash-Kern (Server-Allowlist) ausgenommen.
    try {
      if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
        var _tzn = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (_tzn) fp.tzn = String(_tzn).slice(0, 40);
      }
    } catch(_) {}

    // B3: Headless-Geometrie — innerWidth/Height ins fp. Server berechnet
    // chrome_ui_h = oh - ih (klassischer Headless-Tell: keine Chrome-UI-Hoehe).
    try {
      fp.iw = window.innerWidth || 0;
      fp.ih = window.innerHeight || 0;
    } catch(_) {}

    // B5: Connection-API-Praesenz als explizites Flag — 'API fehlt' (Datacenter-
    // Indikator) vs. '4g'. Server gated strikt auf Chromium (Firefox/Safari haben
    // die API legitim nicht).
    fp.hco = navigator.connection ? 1 : 0;

    // B7: mimeTypes-Laenge (Konsistenz-Check gegen pl=plugins.length) +
    // native-fn-toString-Tampering-Flag (Anti-Detect-Frameworks patchen native
    // Funktionen). Reine data-Felder, vom fp_hash ausgenommen.
    try {
      fp.ml = navigator.mimeTypes ? navigator.mimeTypes.length : 0;
    } catch(_) {}
    try {
      // Probe: native Funktionen tragen normalerweise '[native code]' im toString.
      // Wenn eine zentrale native Funktion gepatcht ist, deutet das auf Tampering.
      var _nativeOk = 1;
      function _isNative(fn) {
        try { return typeof fn === 'function' && /\{\s*\[native code\]\s*\}/.test(Function.prototype.toString.call(fn)); }
        catch(_e) { return false; }
      }
      if (!_isNative(Function.prototype.toString) ||
          !_isNative(navigator.permissions && navigator.permissions.query) ||
          !_isNative(window.requestAnimationFrame)) {
        _nativeOk = 0;
      }
      fp.nt = _nativeOk;
    } catch(_) {}

    function _fpGpu() {
      // GPU
      try {
        var c = document.createElement('canvas');
        var gl = c.getContext('webgl') || c.getContext('experimental-webgl');
        if (gl) {
          var d = gl.getExtension('WEBGL_debug_renderer_info');
          if (d) fp.gpu = gl.getParameter(d.UNMASKED_RENDERER_WEBGL) || '';
          // B4: WebGL VENDOR (unterscheidet echte GPU-Stacks von Software-Renderern)
          // + DJB2-Hash über ~15 stabile gl.getParameter-Werte (gleicher gl-Context,
          // kein Extra-Canvas). Clusterfähig analog cvh → webgl_farm.
          if (d) {
            var _gpv = gl.getParameter(d.UNMASKED_VENDOR_WEBGL);
            if (_gpv) fp.gpv = String(_gpv).slice(0, 60);
          }
          try {
            var _gp = [
              gl.MAX_TEXTURE_SIZE, gl.MAX_CUBE_MAP_TEXTURE_SIZE, gl.MAX_RENDERBUFFER_SIZE,
              gl.MAX_VERTEX_ATTRIBS, gl.MAX_VERTEX_UNIFORM_VECTORS, gl.MAX_FRAGMENT_UNIFORM_VECTORS,
              gl.MAX_VARYING_VECTORS, gl.MAX_TEXTURE_IMAGE_UNITS, gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS,
              gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS, gl.MAX_VIEWPORT_DIMS,
              gl.ALIASED_LINE_WIDTH_RANGE, gl.ALIASED_POINT_SIZE_RANGE,
              gl.RED_BITS, gl.GREEN_BITS, gl.BLUE_BITS, gl.DEPTH_BITS
            ];
            var _gs = '';
            for (var _gi = 0; _gi < _gp.length; _gi++) {
              var _gv = gl.getParameter(_gp[_gi]);
              if (_gv && _gv.length !== undefined && typeof _gv !== 'string') {
                // typed arrays / ranges → komma-joinen
                _gs += Array.prototype.join.call(_gv, ',') + '|';
              } else {
                _gs += _gv + '|';
              }
            }
            var _gh = 5381;
            for (var _gj = 0; _gj < _gs.length; _gj++) {
              _gh = ((_gh << 5) + _gh + _gs.charCodeAt(_gj)) & 0xFFFFFFFF;
            }
            fp.gph = (_gh >>> 0).toString(16);
          } catch(_) {}
        }
      } catch(_) {}
    }

    function _fpMisc() {
      fp.ac = typeof AudioContext !== 'undefined' || typeof webkitAudioContext !== 'undefined' ? 1 : 0;

      // Network info (Fraud: Datacenter hat oft keine Connection API)
      var conn = navigator.connection;
      if (conn) {
        if (conn.effectiveType) fp.ct = conn.effectiveType;
        if (conn.rtt !== undefined) fp.rtt = conn.rtt;
        if (conn.downlink !== undefined) fp.dl = conn.downlink;
        if (conn.saveData) fp.sav = 1;
      }

  	    // UA Client Hints (Modern browsers only)
  	    var uad = navigator.userAgentData;
  	    highEntropyPromise = null;
  	    if (uad) {
  	      if (uad.brands && uad.brands.length) fp.br = uad.brands.map(function(b) { return b.brand + '/' + b.version; }).join(',');
  	      if (uad.mobile !== undefined) fp.mob = uad.mobile ? 1 : 0;
  	      if (uad.platform) fp.uap = uad.platform;
  	      if (typeof uad.getHighEntropyValues === 'function') {
  	        highEntropyPromise = uad.getHighEntropyValues([
  	          'architecture',
  	          'bitness',
  	          'model',
  	          'platformVersion',
  	          'fullVersionList'
  	        ]).then(function(ua) {
  	          if (ua.architecture) fp.uaa = ua.architecture;
  	          if (ua.bitness) fp.uab = ua.bitness;
  	          if (ua.model) fp.uam = ua.model;
  	          if (ua.platformVersion) fp.uapv = ua.platformVersion;
  	          if (ua.fullVersionList && ua.fullVersionList.length) {
  	            fp.uaf = ua.fullVersionList.map(function(b) {
  	              return b.brand + '/' + b.version;
  	            }).join(',');
  	          }
  	        }).catch(function() {});
  	      }
  	    }

      // Headless/Automation detection probes
      fp.chr = (window.chrome && typeof window.chrome === 'object') ? 1 : 0;
      fp.nf = typeof Notification !== 'undefined' ? (Notification.permission || 'na') : 'no';

      // Stealth webdriver descriptor & Client-Hints platform mismatch probes (boolean only)
      try {
        if (uad && uad.platform === 'Linux' && /Windows NT|Macintosh/.test(navigator.userAgent || '')) {
          fp.chm = 1;
        }
        var wdOwn = Object.getOwnPropertyDescriptor(navigator, 'webdriver');
        var wdProto = typeof Navigator !== 'undefined' && Navigator.prototype
          ? Object.getOwnPropertyDescriptor(Navigator.prototype, 'webdriver') : null;
        if (wdOwn || (wdProto && wdProto.get && Function.prototype.toString.call(wdProto.get).indexOf('[native code]') < 0)) {
          fp.wts = 1;
        }
      } catch(_) {}

      // B2: permissions.query(notifications).state — Mismatch gegen nf erkennt
      // Headless-Chrome (nf='denied' während pq='prompt'). Async wie
      // highEntropyPromise (kein Blocking); Dispatch wartet zusätzlich darauf.
      permQueryPromise = null;
      try {
        if (navigator.permissions && typeof navigator.permissions.query === 'function') {
          permQueryPromise = navigator.permissions.query({ name: 'notifications' }).then(function(st) {
            if (st && st.state) fp.pq = String(st.state).slice(0, 12);
          }).catch(function() {});
        }
      } catch(_) {}

      // Automation artifacts: ChromeDriver, Selenium, Playwright, Puppeteer
      var aa = [];
      try {
        var keys = Object.getOwnPropertyNames(window);
        for (var i = 0; i < keys.length; i++) {
          var k = keys[i];
          if (/^cdc_|^__selenium|^__wdc_|^__playwright|^__pwInitScripts|^__puppeteer|^_phantom|^callPhantom|^_Recaptcha|^__nightmare|^domAutomation|^domAutomationController/.test(k)) {
            aa.push(k.substring(0, 20));
          }
        }
        if (document.__selenium_unwrapped || document.__webdriver_evaluate || document.__driver_evaluate) aa.push('doc_sel');
        if (window.emit || window.spawn) aa.push('cjs');
        if (navigator.languages && navigator.languages.length === 0) aa.push('no_langs');
      } catch(_) {}
      if (aa.length) fp.aa = aa.join(',');
    }

    function _fpCanvas() {
      // Canvas fingerprint hash — headless renderers produce distinct pixel output
      try {
        var cv = document.createElement('canvas');
        cv.width = 200; cv.height = 50;
        var ctx = cv.getContext('2d');
        if (ctx) {
          ctx.textBaseline = 'top';
          ctx.font = '14px Arial';
          ctx.fillStyle = '#f60';
          ctx.fillRect(0, 0, 62, 20);
          ctx.fillStyle = '#069';
          ctx.fillText('slewo.fp', 2, 15);
          ctx.fillStyle = 'rgba(102,204,0,0.7)';
          ctx.fillText('slewo.fp', 4, 17);
          var d = cv.toDataURL();
          // Simple hash (DJB2) — not crypto, just collision-resistant enough for clustering
          var h = 5381;
          for (var j = 0; j < d.length; j++) {
            h = ((h << 5) + h + d.charCodeAt(j)) & 0xFFFFFFFF;
          }
          fp.cvh = (h >>> 0).toString(16);
        }
      } catch(_) {}
    }

    function _fpAudio() {
      // AudioContext fingerprint hash — Headless-Browser erzeugen andere Frequenzdaten
  	    var acSent = false;
  	    var fpDispatched = false;
  	    function sendFp() {
  	      if (acSent) return;
  	      acSent = true;
  	      function dispatch() {
  	        if (fpDispatched) return;
  	        fpDispatched = true;
  	        send({ type: 'fp', data: fp, url: location.pathname });
  	      }
  	      // Auf die async-Probes (highEntropy + B2 permissions.query) warten, aber
  	      // hart bei 250ms cappen — kein Blocking, fehlende Felder werden weggelassen.
  	      var _waits = [];
  	      if (highEntropyPromise) _waits.push(highEntropyPromise);
  	      if (permQueryPromise) _waits.push(permQueryPromise);
  	      if (_waits.length) {
  	        Promise.all(_waits).then(dispatch);
  	        setTimeout(dispatch, 250);
  	      } else {
  	        dispatch();
  	      }
  	    }

      try {
        var actx = new (window.AudioContext || window.webkitAudioContext)();
        var osc = actx.createOscillator();
        var comp = actx.createDynamicsCompressor();
        var analyser = actx.createAnalyser();
        analyser.fftSize = 256;
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(10000, actx.currentTime);
        osc.connect(comp);
        comp.connect(analyser);
        analyser.connect(actx.destination);
        osc.start(0);
        setTimeout(function() {
          try {
            var fdata = new Float32Array(analyser.frequencyBinCount);
            analyser.getFloatFrequencyData(fdata);
            var ach = 5381;
            for (var fi = 0; fi < fdata.length; fi++) {
              if (isFinite(fdata[fi])) ach = ((ach << 5) + ach + Math.round(fdata[fi] * 1000)) & 0xFFFFFFFF;
            }
            fp.ach = (ach >>> 0).toString(16);
          } catch(_) {}
          try { osc.stop(); actx.close(); } catch(_) {}
          sendFp();
        }, 50);
        // Fallback falls AudioContext hängt
        setTimeout(sendFp, 200);
      } catch(_) {
        sendFp();
      }
    }

    var _fpStages = [_fpGpu, _fpMisc, _fpCanvas, _fpAudio];
    (function _fpNext(i) {
      if (i >= _fpStages.length) return;
      _fpYield(function() {
        try { _fpStages[i](); } catch(_) {}
        _fpNext(i + 1);
      });
    })(0);
  }

  if (document.readyState === 'complete') {
    setTimeout(sendFingerprint, 500);
  } else {
    window.addEventListener('load', function() {
      setTimeout(sendFingerprint, 500);
    });
  }

  // ===================================================================
  // 14. Auto-Detection Events (GA4-aequivalent)
  //     Site Search, File Download, Outbound Click
  // ===================================================================

  // Site Search: URL enthält /suche oder ?suche=/?q=/?search= Parameter.
  // Läuft beim Laden UND nach jeder AJAX-Navigation (getlink) — bis 2026-10-11 nur beim Laden,
  // dabei startet das Suchfeld die Suche per getlink: gut die Hälfte der Suchen fehlte.
  // Weitergeleitete Suchen (Marke/Kategorie/Service) meldet der Server über window.__slSearchRedirect.
  var _searchRids = {};
  var _lastSearch = null;
  var _lastTerm = null;
  var _searchByUrl = {};
  function searchTermFromUrl() {
    var path = location.pathname.toLowerCase();
    var params = location.search.toLowerCase();
    var searchTerm = null;
    if (path.indexOf('/suche') >= 0 || path.indexOf('/search') >= 0) {
      try {
        var sp = new URLSearchParams(location.search);
        searchTerm = sp.get('suche') || sp.get('q') || sp.get('search') || sp.get('query') || sp.get('s') || '';
      } catch(_) { searchTerm = ''; }
    } else if (params.indexOf('suche=') >= 0 || params.indexOf('q=') >= 0 || params.indexOf('search=') >= 0) {
      try {
        var sp2 = new URLSearchParams(location.search);
        searchTerm = sp2.get('suche') || sp2.get('q') || sp2.get('search') || '';
      } catch(_) {}
    }
    return searchTerm;
  }
  function trackSearch(soft) {
    var sm = window.__slSearchMeta;
    var sr = window.__slSearchRedirect;
    var hasMeta = sm && typeof sm === 'object';
    var searchData = null;
    var urlKey = location.pathname + location.search;
    // Zurück auf eine Ergebnisliste (Seiten-Cache, Skripte laufen erst danach): Zuordnung der Klicks behalten
    _lastSearch = (soft && !hasMeta && !sr && _searchByUrl[urlKey]) || null;
    if (_lastSearch) _lastTerm = _lastSearch.term;
    if (sr && typeof sr === 'object' && sr.q) {
      searchData = {
        term: String(sr.q).substring(0, 200),
        redirect: String(sr.art || '').substring(0, 20),
        ziel: String(sr.ziel || '').substring(0, 200)
      };
      if (sr.rid) searchData.request_id = String(sr.rid).substring(0, 24);
    } else {
      var searchTerm = searchTermFromUrl();
      // Seite ohne Suche beendet den Suchvorgang: derselbe Begriff danach ist eine neue Suche
      if (searchTerm === null || searchTerm === '') { _lastTerm = null; return; }
      // Nach AJAX-Navigation nur mit frischer Ergebnisliste — sonst zählte Zurück (Seiten-Cache) doppelt
      if (soft && !hasMeta) return;
      searchData = { term: searchTerm.substring(0, 200) };
      // Ergebnis-Anzahl (von PHP via window.__slSearchCount bereitgestellt)
      if (typeof window.__slSearchCount !== 'undefined') {
        searchData.num_results = window.__slSearchCount;
      }
      // Suchvorgang zuordenbar machen: Ergebnisliste, Abruf, Captcha-Zustand, gezeigte Liste, Stand
      if (hasMeta) {
        if (sm.id) searchData.search_id = sm.id;
        if (sm.rid) searchData.request_id = String(sm.rid).substring(0, 24);
        searchData.light = sm.light ? 1 : 0;
        if (sm.rs) searchData.result_set = String(sm.rs).substring(0, 24);
        if (sm.rel) searchData.release = String(sm.rel).substring(0, 40);
        if (sm.top) searchData.top = String(sm.top).substring(0, 300);
      }
    }
    // Verbrauchen: die Globals stammen von genau einer Seite
    window.__slSearchMeta = undefined;
    window.__slSearchRedirect = undefined;
    window.__slSearchCount = undefined;
    if (!searchData.redirect) {
      _lastSearch = { id: searchData.search_id || 0, rid: searchData.request_id || '', term: searchData.term };
      _searchByUrl[urlKey] = _lastSearch;
    }
    if (searchData.request_id) {
      if (_searchRids[searchData.request_id]) return;
      _searchRids[searchData.request_id] = 1;
    }
    // Filter-/Sortierwechsel per AJAX liefert eine neue Ergebnisliste zum selben Begriff — keine neue Suche
    if (soft && !searchData.redirect && searchData.term === _lastTerm) return;
    _lastTerm = searchData.term;
    searchData.nav = soft ? 'ajax' : 'load';
    send({
      type: 'search',
      data: searchData,
      url: location.pathname
    });
  }
  trackSearch(false);

  // Klick in der Suchergebnisliste: Position und Artikel (Klickrate je Platz, Relevanz aus echten Klicks)
  document.addEventListener('click', function(e) {
    if (!_lastSearch || !e.target.closest) return;
    var li = e.target.closest('#content_usicht>ul>li');
    var a = e.target.closest('a[href]');
    if (!li || !a) return;
    var m = (a.getAttribute('href') || '').match(/-(\d{9})\.html/);
    send({
      type: 'click',
      data: {
        sub: 'search_result',
        pos: Array.prototype.indexOf.call(li.parentNode.children, li) + 1,
        anr: m ? m[1] : '',
        search_id: _lastSearch.id,
        request_id: _lastSearch.rid
      },
      url: location.pathname
    });
  }, { passive: true, capture: true });

  // File Download: Click auf Links mit Datei-Extension
  var _dlExts = /\.(pdf|xlsx|xls|csv|zip|gz|doc|docx|pptx|rar)$/i;
  document.addEventListener('click', function(e) {
    var a = e.target.closest('a[href]');
    if (!a) return;
    var href = a.getAttribute('href') || '';
    if (_dlExts.test(href.split('?')[0])) {
      var filename = href.split('/').pop().split('?')[0];
      send({
        type: 'file_download',
        data: {
          url: href.substring(0, 500),
          name: (filename || '').substring(0, 100),
          ext: (filename.split('.').pop() || '').toLowerCase()
        },
        url: location.pathname
      });
    }
  }, { passive: true });

  // Outbound Click: Click auf externe Links
  document.addEventListener('click', function(e) {
    var a = e.target.closest('a[href]');
    if (!a) return;
    var href = a.getAttribute('href') || '';
    // Nur absolute URLs mit anderem Hostname
    if (href.indexOf('http') !== 0) return;
    try {
      var linkHost = new URL(href).hostname;
      if (linkHost && linkHost !== location.hostname) {
        send({
          type: 'click',
          data: {
            sub: 'outbound',
            dest: href.substring(0, 500),
            host: linkHost,
            text: (a.textContent || '').trim().substring(0, 100)
          },
          url: location.pathname
        });
      }
    } catch(_) {}
  }, { passive: true });

  // ===================================================================
  // 15. Exit-Intent Detection (v4.5, Survey deaktiviert)
  // ===================================================================
  //
  // Survey-Popup entfernt — User-Wunsch: zu nervig. Tracking bleibt
  // erhalten, damit "wie viele User wollten gehen" weiter messbar ist
  // (exit_intent-Events ins Beacon, keine UI-Unterbrechung).

  var _exitIntentFired = false;
  var _isMobile = /Mobi|Android/i.test(navigator.userAgent);

  // Desktop: Maus verlässt Viewport nach oben
  if (!_isMobile) {
    document.addEventListener('mouseout', function(e) {
      if (_exitIntentFired) return;
      // Nur oben raus (y <= 0 oder relatedTarget null = Viewport verlassen)
      if (e.clientY > 5) return;
      if (e.relatedTarget || e.toElement) return;
      // Erst nach 30s auf der Seite
      if ((Date.now() - startTime) < 30000) return;

      _exitIntentFired = true;
      send({
        type: 'exit_intent',
        data: {
          url: location.pathname,
          scroll_pct: maxScroll,
          time_on_page: Math.round((Date.now() - startTime) / 1000),
          had_interaction: hasInteracted ? 1 : 0,
          trigger: 'mouseout'
        },
        url: location.pathname
      });
    }, { passive: true });
  }

  // Mobile: visibilitychange (Tab-Close, App-Switch)
  if (_isMobile) {
    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState !== 'hidden') return;
      if (_exitIntentFired) return;
      if ((Date.now() - startTime) < 30000) return;

      _exitIntentFired = true;
      send({
        type: 'exit_intent',
        data: {
          url: location.pathname,
          scroll_pct: maxScroll,
          time_on_page: Math.round((Date.now() - startTime) / 1000),
          had_interaction: hasInteracted ? 1 : 0,
          trigger: 'visibility'
        },
        url: location.pathname
      });
    });

    // Mobile: Back-Button-Navigation
    window.addEventListener('popstate', function() {
      if (_exitIntentFired) return;
      if ((Date.now() - startTime) < 30000) return;

      _exitIntentFired = true;
      send({
        type: 'exit_intent',
        data: {
          url: location.pathname,
          scroll_pct: maxScroll,
          time_on_page: Math.round((Date.now() - startTime) / 1000),
          had_interaction: hasInteracted ? 1 : 0,
          trigger: 'back_button'
        },
        url: location.pathname
      });
    });
  }

  // ===================================================================
  // 16. Public API: window.slewoTrack() (v4.4)
  // ===================================================================

  // Ermöglicht Custom Events vom Shop-Code:
  //   window.slewoTrack('video_start', { video_id: 'abc', title: 'Matratze Test' })
  //   window.slewoTrack('newsletter_signup', { source: 'footer' })
  window.slewoTrack = function(eventName, eventData) {
    if (!eventName || typeof eventName !== 'string') return;
    // Event-Name validieren: nur lowercase alphanumerisch + underscore
    if (!/^[a-z][a-z0-9_]{1,29}$/.test(eventName)) return;
    send({
      type: eventName,
      data: eventData || {},
      url: location.pathname
    });
  };

  // ===================================================================
  // 17. Cross-Device Link-Sharing (v4.5)
  // ===================================================================
  // Wenn Besucher eine Produkt-URL kopieren (z.B. "Schatz, guck mal dieses Bett"),
  // wird ein _slxd Token angehaengt. Oeffnet der Partner den Link auf einem anderen
  // Geraet, werden die Sessions verknuepft → bessere Attribution bei Moebel-Kaeufen.

  (function() {
    // Token generieren: 16 Hex-Zeichen aus Crypto API
    function _xdToken() {
      var arr = new Uint8Array(8);
      crypto.getRandomValues(arr);
      return Array.from(arr, function(b) { return b.toString(16).padStart(2, '0'); }).join('');
    }

    function _xdEditable(el) {
      try {
        if (!el || el.nodeType !== 1) return false;
        var tn = el.tagName;
        return tn === 'INPUT' || tn === 'TEXTAREA' || tn === 'SELECT' || !!el.isContentEditable;
      } catch(_) { return false; }
    }

    // Copy-Event: URL mit _slxd Token in die Zwischenablage
    document.addEventListener('copy', function(ev) {
      // Aus Eingabefeldern nie eingreifen: Firefox liefert dort für getSelection() einen leeren String, die Seitenadresse
      // ersetzte dann den kopierten Text (Adresse, Gutscheincode, E-Mail). Ebenso nicht auf Warenkorb und Kasse.
      if (_xdEditable(ev && ev.target) || _xdEditable(document.activeElement)) return;
      var sel = window.getSelection();
      // Nur wenn nichts selektiert oder die URL in der Adressleiste kopiert wird
      // Copy-Event feuert auch bei normaler Textselektion — wir pruefen ob die
      // aktuelle URL eine Produktseite ist (hat .html oder /p/ im Pfad)
      var path = location.pathname;
      if (!/\.html|\/p\//.test(path) || /^\/(?:kasse|basket|warenkorb|checkout)/i.test(path)) return;

      // Token nur einmal pro Session generieren
      if (!window._slxdToken) {
        window._slxdToken = _xdToken();
        // Token an Backend melden
        send({
          type: 'xdevice_share',
          data: {
            token: window._slxdToken,
            url: path
          },
          url: path
        });
      }

      // URL mit Token in Clipboard ueberschreiben (nur wenn keine Text-Selektion)
      if (sel && sel.toString().length > 0) return;
      try {
        var url = new URL(location.href);
        url.searchParams.set('_slxd', window._slxdToken);
        navigator.clipboard.writeText(url.toString());
      } catch(_) {}
    });

    // Bei Seitenaufruf: _slxd Token aus URL lesen und an Backend senden
    try {
      var sp = new URLSearchParams(location.search);
      var xdToken = sp.get('_slxd');
      if (xdToken && /^[0-9a-f]{16}$/.test(xdToken)) {
        send({
          type: 'xdevice_link',
          data: { token: xdToken },
          url: location.pathname
        });
        // Token aus URL entfernen (clean URL)
        sp.delete('_slxd');
        var clean = location.pathname + (sp.toString() ? '?' + sp.toString() : '') + location.hash;
        if (history.replaceState) history.replaceState(null, '', clean);
      }
    } catch(_) {}
  })();

  // ===================================================================
  // 18. Honeypot Bot-Detection (v4.7)
  // ===================================================================
  // Faengt Bots die versteckte Felder befuellen (Trap 2) oder unsichtbare
  // Elemente anklicken (Trap 3). Trap 1 (Link-Follow) laeuft server-seitig.

  (function() {
    // Trap 2: Formular-Feld-Watcher — Bots die alle Inputs befuellen
    var hpField = document.querySelector('[data-hp="1"] input');
    if (hpField) {
      hpField.addEventListener('change', function() {
        send({ type: 'honeypot', data: { t: 'form_fill' }, url: location.pathname });
      });
    }

    // Trap 3: JS-Only Click Trap — nur per programmatischem DOM-Zugriff erreichbar
    var trap = document.createElement('div');
    trap.setAttribute('aria-hidden', 'true');
    trap.setAttribute('tabindex', '-1');
    trap.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:0;height:0;overflow:hidden';
    trap.addEventListener('click', function() {
      send({ type: 'honeypot', data: { t: 'js_click' }, url: location.pathname });
    });
    document.body.appendChild(trap);
  })();

  // ===================================================================
  // 19. Service Worker Registration (Beacon Relay + Background Sync)
  // ===================================================================

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw-beacon.js', { scope: '/' })
      .catch(function() {});
  }
})();
